// Fuel Service
// Provides utility functions for fuel-related API calls

import { apiGet, apiPost } from './apiService';
import moment from 'moment';

/**
 * Convert milliseconds to HH:MM:SS format
 * @param {number} ms - Milliseconds
 * @returns {string} Time string in HH:MM:SS format
 */
const msToTimeString = (ms) => {
  if (ms === null || ms === undefined) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Convert HH:MM:SS format to milliseconds
 * @param {string} timeStr - Time string in HH:MM:SS format
 * @returns {number|null} Milliseconds or null if invalid
 */
const timeStringToMs = (timeStr) => {
  if (!timeStr) return null;
  const parts = timeStr.split(':');
  if (parts.length !== 3) return null;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);
  if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;
  return (hours * 3600 + minutes * 60 + seconds) * 1000;
};

/**
 * Fetch daily IoT fuel data summary for assets
 * @param {string} assetId - Asset ID
 * @param {number} startTimestamp - Start timestamp in milliseconds
 * @param {number} endTimestamp - End timestamp in milliseconds
 * @returns {Promise<{success: boolean, data: Array, error: string|null}>}
 */
export const fetchDailyIotSummary = async (assetId, startTimestamp, endTimestamp) => {
  try {
    console.log(`Fetching daily IoT summary for asset ${assetId} from ${startTimestamp} to ${endTimestamp}`, {
      assetId,
      assetIdType: typeof assetId,
      startTimestamp,
      endTimestamp,
      startDate: moment(startTimestamp).utc().format('YYYY-MM-DD HH:mm:ss') + ' UTC',
      endDate: moment(endTimestamp).utc().format('YYYY-MM-DD HH:mm:ss') + ' UTC',
    });

    // Validate inputs
    if (!assetId) {
      return { success: false, data: [], error: 'Asset ID is required' };
    }
    
    if (!startTimestamp || !endTimestamp || startTimestamp >= endTimestamp) {
      return { success: false, data: [], error: 'Invalid timestamp range' };
    }

    const endpoint = `/daily-fuel-data/all-device-report/${startTimestamp}/${endTimestamp}`;
    
    const requestBody = {
      assets: [assetId],
      locations: ['all'],
      subLocations: ['all'],
      assetTypes: [],
      assetSubTypes: [],
      assetModels: [],
      frequency: 'Daily',
    };
    
    console.log('Daily IoT Summary API Request:', {
      endpoint,
      requestBody,
    });
    
    const result = await apiPost(endpoint, requestBody);

    console.log('Daily IoT Summary API Response:', {
      success: result.success,
      hasError: !!result.error,
      error: result.error,
      hasData: !!result.data,
      dataType: Array.isArray(result.data) ? 'array' : typeof result.data,
      dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
    });

    if (!result.success) {
      return { success: false, data: [], error: result.error || 'API request failed' };
    }

    // Log the full response structure to understand it
    console.log('Daily IoT Summary API Response:', {
      hasData: !!result.data,
      dataType: Array.isArray(result.data) ? 'array' : typeof result.data,
      dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
      firstItem: Array.isArray(result.data) && result.data.length > 0 ? result.data[0] : null,
      firstItemKeys: Array.isArray(result.data) && result.data.length > 0 && typeof result.data[0] === 'object' 
        ? Object.keys(result.data[0]) 
        : 'N/A',
    });

    return { success: true, data: result.data || [], error: null };
  } catch (error) {
    console.error('Error fetching daily IoT summary:', error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Fetch IoT device data for a specific period (max 48 hours)
 * @param {string} deviceId - Device ID (alt name of sensor)
 * @param {number} startTimestamp - Start timestamp in milliseconds
 * @param {number} endTimestamp - End timestamp in milliseconds
 * @param {string} assetId - Asset ID
 * @returns {Promise<{success: boolean, data: Array, error: string|null}>}
 */
export const fetchIotDeviceDataInPeriod = async (deviceId, startTimestamp, endTimestamp, assetId) => {
  try {
    console.log(`Fetching IoT device data for device ${deviceId} from ${startTimestamp} to ${endTimestamp}`);

    // Check if period is more than 48 hours
    const periodHours = (endTimestamp - startTimestamp) / (1000 * 60 * 60);
    if (periodHours > 48) {
      return {
        success: false,
        data: [],
        error: `Period exceeds 48 hours limit. Period: ${periodHours.toFixed(2)} hours`
      };
    }

    const endpoint = `/iot-device-data/in-period/${deviceId}/${startTimestamp}/${endTimestamp}/${assetId}`;
    
    const result = await apiGet(endpoint);

    if (!result.success) {
      return { success: false, data: [], error: result.error };
    }

    // Log the full response structure to understand it
    console.log('IoT Device Data in Period API Response:', {
      hasData: !!result.data,
      dataType: Array.isArray(result.data) ? 'array' : typeof result.data,
      dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
      keys: result.data && typeof result.data === 'object' ? Object.keys(result.data) : 'N/A',
      fullResponse: result.data,
    });

    // Check if response has summary and raw data
    if (result.data && typeof result.data === 'object' && !Array.isArray(result.data)) {
      // Response is an object, might have summary and raw data
      if (result.data.summary) {
        console.log('Found summary data in response:', result.data.summary);
      }
      if (result.data.raw) {
        console.log('Found raw data in response (array length):', Array.isArray(result.data.raw) ? result.data.raw.length : 'N/A');
      }
    }

    return { success: true, data: result.data || [], error: null };
  } catch (error) {
    console.error('Error fetching IoT device data:', error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Fetch asset details by asset ID
 * @param {string} assetId - Asset ID
 * @returns {Promise<{success: boolean, asset: Object|null, error: string|null}>}
 */
export const fetchAssetDetails = async (assetId) => {
  try {
    console.log(`Fetching asset details for ID: ${assetId}`);
    
    const result = await apiGet(`/asset/${assetId}`, {
      headers: {
        'Accept': '*/*',
      },
    });

    if (!result.success) {
      return { success: false, asset: null, error: result.error };
    }

    return { success: true, asset: result.data || null, error: null };
  } catch (error) {
    console.error('Error fetching asset details:', error);
    return { success: false, asset: null, error: error.message };
  }
};

/**
 * Get device ID (alt name) from asset data
 * The device ID can be found in the asset's IoT device association
 * @param {string} assetCode - Asset code
 * @returns {Promise<{success: boolean, deviceId: string|null, error: string|null}>}
 */
export const getDeviceIdFromAsset = async (assetCode, assetId = null) => {
  try {
    // Use provided assetId if available, otherwise fetch it
    let finalAssetId = assetId;
    if (!finalAssetId) {
      const assetResult = await findAssetIdByCode(assetCode);
      if (!assetResult.success) {
        return { success: false, deviceId: null, error: assetResult.error };
      }
      finalAssetId = assetResult.assetId;
    }
    
    // Fetch asset details to get device ID
    const assetDetailsResult = await fetchAssetDetails(finalAssetId);
    if (!assetDetailsResult.success) {
      // Fallback: Try to get device ID from daily summary
      console.log('Failed to fetch asset details, trying daily summary fallback');
      const endTimestamp = moment().valueOf();
      const startTimestamp = moment().subtract(7, 'days').valueOf();
      
      const summaryResult = await fetchDailyIotSummary(finalAssetId, startTimestamp, endTimestamp);
      if (summaryResult.success && summaryResult.data.length > 0) {
        const deviceId = summaryResult.data[0].device_id;
        if (deviceId) {
          console.log(`Found device ID from daily summary: ${deviceId}`);
          return { success: true, deviceId: deviceId, error: null };
        }
      }
      
      return { success: false, deviceId: null, error: assetDetailsResult.error };
    }

    const asset = assetDetailsResult.asset;
    
    // Debug: Log asset structure to understand what we're working with
    console.log('Asset details received:', {
      assetId: asset._id,
      assetCode: asset.assetCode,
      tagDevice: asset.tagDevice,
      tagDeviceId: asset.tagDeviceId,
      hasTagDeviceId: !!asset.tagDeviceId,
      parent: asset.parent,
      hasParent: !!asset.parent,
      dependents: asset.dependents ? asset.dependents.length : 0,
      fuelSensorCalibrationList: asset.fuelSensorCalibrationList,
      keys: Object.keys(asset),
    });
    
    // Try to get device ID from asset object
    // Device ID comes from tagDeviceId for assets where tagDevice is true
    if (asset.tagDevice === true) {
      if (asset.tagDeviceId) {
        console.log(`Found device ID from asset tagDeviceId: ${asset.tagDeviceId}`);
        return { success: true, deviceId: asset.tagDeviceId, error: null };
      } else {
        console.warn('Asset has tagDevice: true but tagDeviceId is null/undefined');
      }
    }
    
    // Check dependents array for a dependent asset with isTagDevice: true
    // The device ID is in tagDeviceId of the dependent asset where assetModel.isTagDevice === true
    if (asset.dependents && Array.isArray(asset.dependents) && asset.dependents.length > 0) {
      console.log(`Asset has ${asset.dependents.length} dependent(s), checking for tagDevice...`);
      
      for (const dependent of asset.dependents) {
        const isTagDevice = dependent.assetModel && dependent.assetModel.isTagDevice === true;
        console.log('Checking dependent:', {
          assetId: dependent._id,
          assetCode: dependent.assetCode,
          isTagDevice: isTagDevice,
          tagDeviceId: dependent.tagDeviceId,
          altName: dependent.altName,
        });
        
        if (isTagDevice && dependent.tagDeviceId) {
          console.log(`Found device ID from dependent asset tagDeviceId: ${dependent.tagDeviceId}`);
          return { success: true, deviceId: dependent.tagDeviceId, error: null };
        }
      }
      
      console.log('No dependent asset with isTagDevice: true and tagDeviceId found');
    } else {
      console.log('Asset has no dependents array or it is empty');
    }
    
    // If this asset doesn't have tagDevice, check if it has a parent asset
    // The device ID might be on the parent/dependent asset
    if (asset.parent) {
      console.log(`Asset has parent: ${asset.parent}, fetching parent asset details...`);
      try {
        const parentAssetResult = await fetchAssetDetails(asset.parent);
        if (parentAssetResult.success && parentAssetResult.asset) {
          const parentAsset = parentAssetResult.asset;
          console.log('Parent asset details:', {
            assetId: parentAsset._id,
            assetCode: parentAsset.assetCode,
            tagDevice: parentAsset.tagDevice,
            tagDeviceId: parentAsset.tagDeviceId,
          });
          
          if (parentAsset.tagDevice === true && parentAsset.tagDeviceId) {
            console.log(`Found device ID from parent asset tagDeviceId: ${parentAsset.tagDeviceId}`);
            return { success: true, deviceId: parentAsset.tagDeviceId, error: null };
          }
        }
      } catch (error) {
        console.error('Error fetching parent asset:', error);
      }
    }
    
    // If asset doesn't have tagDevice enabled or tagDeviceId is missing, try daily summary fallback
    console.log('Trying daily summary fallback to get device ID...');
    const endTimestamp = moment().valueOf();
    const startTimestamp = moment().subtract(7, 'days').valueOf();
    
    const summaryResult = await fetchDailyIotSummary(assetId, startTimestamp, endTimestamp);
    console.log('Daily summary result:', {
      success: summaryResult.success,
      dataLength: summaryResult.data?.length || 0,
      error: summaryResult.error,
    });
    
    if (summaryResult.success && summaryResult.data && summaryResult.data.length > 0) {
      const firstSummary = summaryResult.data[0];
      console.log('First daily summary entry:', {
        device_id: firstSummary.device_id,
        assetCode: firstSummary.asset?.assetCode,
        keys: Object.keys(firstSummary),
      });
      
      const deviceId = firstSummary.device_id;
      if (deviceId) {
        console.log(`Found device ID from daily summary: ${deviceId}`);
        return { success: true, deviceId: deviceId, error: null };
      } else {
        console.warn('Daily summary exists but device_id is missing');
      }
    } else {
      console.warn('Daily summary fetch failed or returned no data:', summaryResult.error);
    }
    
    // Final error message with details
    const errorMsg = asset.tagDevice === false 
      ? 'Asset does not have tagDevice enabled. Device ID is only available for assets with tagDevice: true.'
      : asset.tagDevice === true && !asset.tagDeviceId
      ? 'Asset has tagDevice: true but tagDeviceId is null or undefined. Please check asset configuration.'
      : 'Device ID not found in asset details or daily summary. Asset may not have an associated IoT device or no recent IoT data available.';
    
    console.error('Failed to get device ID:', errorMsg);
    return {
      success: false,
      deviceId: null,
      error: errorMsg
    };
  } catch (error) {
    console.error('Error getting device ID:', error);
    return { success: false, deviceId: null, error: error.message };
  }
};

/**
 * Fetch and combine IoT data for a period using both daily summary and period APIs
 * Intelligently uses daily summary for full 24-hour days and period API for partial days
 * @param {string} assetCode - Asset code (e.g., '20E-290')
 * @param {moment.Moment} startMoment - Start date/time
 * @param {moment.Moment} endMoment - End date/time
 * @param {string} deviceId - Optional device ID. If not provided, will try to fetch it
 * @returns {Promise<{success: boolean, data: Object, error: string|null}>}
 */
export const fetchCombinedIotData = async (assetCode, startMoment, endMoment, deviceId = null) => {
  try {
    // IMPORTANT: startMoment and endMoment are in IST (GMT+5:30) as entered by the user
    // We need to convert them to UTC before making API calls
    // First, ensure the moments are treated as IST, then convert to UTC
    
    // If moments don't have timezone info, assume they're in IST (GMT+5:30)
    // Convert to IST explicitly, then to UTC for API calls
    const startMomentIST = startMoment.clone().utcOffset('+05:30');
    const endMomentIST = endMoment.clone().utcOffset('+05:30');
    
    console.log(`Fetching combined IoT data for ${assetCode}`);
    console.log(`  Input (IST): ${startMoment.format('YYYY-MM-DD HH:mm:ss')} to ${endMoment.format('YYYY-MM-DD HH:mm:ss')}`);
    console.log(`  Converted to IST: ${startMomentIST.format('YYYY-MM-DD HH:mm:ss')} IST to ${endMomentIST.format('YYYY-MM-DD HH:mm:ss')} IST`);
    console.log(`  UTC equivalent: ${startMomentIST.clone().utc().format('YYYY-MM-DD HH:mm:ss')} UTC to ${endMomentIST.clone().utc().format('YYYY-MM-DD HH:mm:ss')} UTC`);

    // Step 1: Get asset ID
    const assetResult = await findAssetIdByCode(assetCode);
    if (!assetResult.success) {
      return { success: false, data: null, error: assetResult.error };
    }
    const assetId = assetResult.assetId;

    // Step 2: Get device ID if not provided
    let finalDeviceId = deviceId;
    if (!finalDeviceId) {
      // Pass assetId to avoid redundant findAssetIdByCode call
      const deviceResult = await getDeviceIdFromAsset(assetCode, assetId);
      if (!deviceResult.success) {
        return { success: false, data: null, error: deviceResult.error };
      }
      finalDeviceId = deviceResult.deviceId;
    }

    // Step 3: Identify full days and partial days (using IST moments)
    const startDay = startMomentIST.clone().startOf('day');
    const endDay = endMomentIST.clone().startOf('day');
    const isSameDay = startDay.isSame(endDay, 'day');
    
    const fullDays = [];
    let firstDayStart = null;
    let firstDayEnd = null;
    let lastDayStart = null;
    let lastDayEnd = null;

    if (isSameDay) {
      // Same day - use period API for the entire range
      firstDayStart = startMomentIST;
      firstDayEnd = endMomentIST;
    } else {
      // Different days
      // First day: from start time to end of day (in IST)
      firstDayStart = startMomentIST;
      firstDayEnd = startMomentIST.clone().endOf('day');

      // Last day: from start of day to end time (in IST)
      lastDayStart = endMomentIST.clone().startOf('day');
      lastDayEnd = endMomentIST;

      // Full days in between (using IST moments)
      let currentDay = startDay.clone().add(1, 'day');
      while (currentDay.isBefore(endDay, 'day')) {
        fullDays.push(currentDay.clone());
        currentDay.add(1, 'day');
      }
    }

    console.log(`Full days: ${fullDays.length}, First day partial: ${!!firstDayStart}, Last day partial: ${!!lastDayStart}`);

    // Step 4: Fetch data for full days using daily summary API
    // Only call daily summary API if there's at least one full 24-hour day
    const dailySummaries = [];
    if (fullDays.length > 0) {
      // Verify that we have at least one complete 24-hour day
      // A full day means: start of day to end of day (24 hours)
      const hasFull24HourDay = fullDays.length > 0;
      
      if (hasFull24HourDay) {
        // Convert local time (IST) to UTC before getting timestamps
        // The API expects UTC timestamps
        const fullDaysStart = fullDays[0].clone().utc().valueOf();
        const fullDaysEnd = fullDays[fullDays.length - 1].clone().endOf('day').utc().valueOf();
        
        console.log(`Fetching daily summaries for ${fullDays.length} full day(s) from ${moment(fullDaysStart).utc().format('YYYY-MM-DD')} to ${moment(fullDaysEnd).utc().format('YYYY-MM-DD')} (UTC)`);
        
        const summaryResult = await fetchDailyIotSummary(assetId, fullDaysStart, fullDaysEnd);
        if (summaryResult.success) {
          // Map summaries to expected dates from fullDays
          // The API might return summaries in a different order or with incorrect/missing dates
          const summariesWithDates = summaryResult.data.map((summary) => {
            // Try to extract date from summary
            const processed = summary.deviceProcessedData || summary;
            
            // Priority 1: Use date_timestamp (in GMT/UTC) and convert to IST (GMT+5:30)
            let istDate = null;
            if (summary.date_timestamp || processed.date_timestamp) {
              const timestamp = summary.date_timestamp || processed.date_timestamp;
              // Parse timestamp as UTC, then convert to IST (GMT+5:30)
              const utcMoment = moment.utc(timestamp);
              // Convert to IST by adding 5 hours 30 minutes
              const istMoment = utcMoment.clone().utcOffset('+05:30');
              istDate = istMoment.format('YYYY-MM-DD');
              
              console.log('Date from timestamp:', {
                timestamp: timestamp,
                utcDate: utcMoment.format('YYYY-MM-DD HH:mm:ss') + ' UTC',
                istDate: istMoment.format('YYYY-MM-DD HH:mm:ss') + ' IST',
                finalDate: istDate,
              });
            }
            
            // Priority 2: Try to extract date from other date fields
            let apiDate = summary.date || processed.date;
            let parsedApiDate = null;
            if (apiDate && !istDate) {
              if (typeof apiDate === 'string') {
                parsedApiDate = moment.utc(apiDate);
              } else if (typeof apiDate === 'number') {
                parsedApiDate = moment.utc(apiDate);
              }
              if (parsedApiDate && parsedApiDate.isValid()) {
                // Convert to IST
                const istMoment = parsedApiDate.clone().utcOffset('+05:30');
                apiDate = istMoment.format('YYYY-MM-DD');
              }
            }
            
            // Use IST date if available, otherwise use parsed API date
            const finalDate = istDate || apiDate;
            
            // Store the assigned date in the summary
            summary._assignedDate = finalDate;
            summary.date = summary._assignedDate; // Set date field for consistency
            
            console.log('Date matching for summary:', {
              date_timestamp: summary.date_timestamp || processed.date_timestamp,
              istDate: istDate,
              apiDate: summary.date || processed.date,
              parsedApiDate: apiDate,
              finalDate: summary._assignedDate,
            });
            
            return summary;
          });
          
          dailySummaries.push(...summariesWithDates);
          console.log(`Fetched ${dailySummaries.length} daily summaries for full days`);
        } else {
          console.warn('Failed to fetch daily summaries:', summaryResult.error);
        }
      } else {
        console.log('No full 24-hour days found, skipping daily summary API call');
      }
    } else {
      console.log('No full days between start and end dates, skipping daily summary API call');
    }

    // Step 5: Fetch data for partial days using period API
    const periodData = [];
    
    // First day (if partial)
    if (firstDayStart && firstDayEnd) {
      // Convert local time (IST) to UTC before getting timestamps
      // The API expects UTC timestamps
      const firstDayStartTs = firstDayStart.clone().utc().valueOf();
      const firstDayEndTs = firstDayEnd.clone().utc().valueOf();
      const periodResult = await fetchIotDeviceDataInPeriod(
        finalDeviceId,
        firstDayStartTs,
        firstDayEndTs,
        assetId
      );
      if (periodResult.success) {
        periodData.push({
          date: firstDayStart.format('YYYY-MM-DD'),
          startTime: firstDayStartTs,
          endTime: firstDayEndTs,
          data: periodResult.data,
          isPartial: true,
          isFirstDay: true,
        });
        console.log(`Fetched period data for first day: ${firstDayStart.format('YYYY-MM-DD')}`);
      } else {
        console.warn('Failed to fetch first day period data:', periodResult.error);
      }
    }

    // Last day (if partial and different from first day)
    if (lastDayStart && lastDayEnd && !isSameDay) {
      // Convert local time (IST) to UTC before getting timestamps
      // The API expects UTC timestamps
      const lastDayStartTs = lastDayStart.clone().utc().valueOf();
      const lastDayEndTs = lastDayEnd.clone().utc().valueOf();
      const periodResult = await fetchIotDeviceDataInPeriod(
        finalDeviceId,
        lastDayStartTs,
        lastDayEndTs,
        assetId
      );
      if (periodResult.success) {
        periodData.push({
          date: lastDayStart.format('YYYY-MM-DD'),
          startTime: lastDayStartTs,
          endTime: lastDayEndTs,
          data: periodResult.data,
          isPartial: true,
          isLastDay: true,
        });
        console.log(`Fetched period data for last day: ${lastDayStart.format('YYYY-MM-DD')}`);
      } else {
        console.warn('Failed to fetch last day period data:', periodResult.error);
      }
    }

    // Step 6: Combine and transform data
    const combinedData = {
      dailySummaries: dailySummaries,
      periodData: periodData,
      deviceId: finalDeviceId,
      assetId: assetId,
    };

    return {
      success: true,
      data: combinedData,
      error: null
    };
  } catch (error) {
    console.error('Error fetching combined IoT data:', error);
    return {
      success: false,
      data: null,
      error: error.message
    };
  }
};

/**
 * Calculate statistics from period IoT data
 * The API response contains deviceProcessedData with summary statistics
 * @param {Array|Object} periodData - Period data from IoT device API
 * @param {number} totalDuration - Total duration of the query period in milliseconds (for stop time calculation)
 * @returns {Object} Calculated statistics
 */
const calculatePeriodStats = (periodData, totalDuration = null) => {
  if (!periodData) {
    return {
      movingTime: null,
      pauseTime: null,
      ignitionTime: null,
      stopTime: null,
      distanceTravelled: null,
      fuelConsumed: null,
      possibleRefuel: null,
      possibleDrain: null,
      avgFuelLtrPerHr: null,
      avgFuelKmPerL: null,
      averageSpeed: null,
    };
  }

  // If period data is an object, check for deviceProcessedData
  if (typeof periodData === 'object' && !Array.isArray(periodData)) {
    // The summary data is in deviceProcessedData
    const processed = periodData.deviceProcessedData || periodData;
    
    console.log('Processing period data:', {
      hasDeviceProcessedData: !!periodData.deviceProcessedData,
      processedKeys: Object.keys(processed),
      fuel_consumed: processed.fuel_consumed,
      fuel_consumption_GNSS: processed.fuel_consumption_GNSS,
      newConsumed: processed.newConsumed,
      fullProcessed: processed,
    });

    // Extract stats from deviceProcessedData
    const movingTime = processed.moving_time || 0;
    const idealTime = processed.ideal_time || 0;
    const ignitionTime = processed.ignition_time || 0;
    // Convert distance from meters to kilometers
    const distanceTravelledMeters = processed.distance_travelled || 0;
    const distanceTravelled = distanceTravelledMeters / 1000; // Convert meters to km
    
    // Get refuel and theft from newConsumed
    const newConsumed = processed.newConsumed || {};
    
    // Fuel consumed might be in newConsumed.fuel_consumed or processed.fuel_consumed
    // Check both locations to find the correct value
    const fuelConsumed = newConsumed.fuel_consumed || processed.fuel_consumed || 0;
    
    console.log('Fuel consumed extraction:', {
      fromNewConsumed: newConsumed.fuel_consumed,
      fromProcessed: processed.fuel_consumed,
      finalValue: fuelConsumed,
    });
    
    const possibleRefuel = newConsumed.total_refuel ? Math.abs(newConsumed.total_refuel) : 0;
    const possibleDrain = newConsumed.total_theft || 0;
    
    // Calculate stop time: total duration - ignition time
    let stopTime = null;
    if (totalDuration && ignitionTime) {
      stopTime = totalDuration - ignitionTime;
    } else if (ignitionTime && movingTime && idealTime) {
      // Fallback: if we have moving and ideal time, stop = ignition - moving - ideal
      stopTime = ignitionTime - movingTime - idealTime;
    }
    
    // Calculate average fuel consumption
    // Avg Fuel Consumed ltr/h = fuel consumed / (ignition_time in hours)
    const ignitionHours = ignitionTime / (1000 * 60 * 60); // Convert ms to hours
    const avgFuelLtrPerHr = ignitionHours > 0 && fuelConsumed > 0
      ? (fuelConsumed / ignitionHours).toFixed(2)
      : null;
    
    // Avg Fuel Consumed km/l = distance / fuel consumed
    const avgFuelKmPerL = fuelConsumed > 0 && distanceTravelled > 0
      ? (distanceTravelled / fuelConsumed).toFixed(2)
      : null;

    const movingHours = movingTime / (1000 * 60 * 60);
    const averageSpeed =
      processed.average_speed ??
      processed.avg_speed ??
      (movingHours > 0 && distanceTravelled > 0 ? (distanceTravelled / movingHours) : null);

    return {
      movingTime: movingTime > 0 ? msToTimeString(movingTime) : null,
      pauseTime: idealTime > 0 ? msToTimeString(idealTime) : null,
      ignitionTime: ignitionTime > 0 ? msToTimeString(ignitionTime) : null,
      stopTime: stopTime > 0 ? msToTimeString(stopTime) : null,
      distanceTravelled: distanceTravelled > 0 ? distanceTravelled : null,
      fuelConsumed: fuelConsumed > 0 ? fuelConsumed : null,
      possibleRefuel: possibleRefuel > 0 ? possibleRefuel : null,
      possibleDrain: possibleDrain > 0 ? possibleDrain : null,
      avgFuelLtrPerHr: avgFuelLtrPerHr,
      avgFuelKmPerL: avgFuelKmPerL,
      averageSpeed: averageSpeed !== null && averageSpeed !== undefined && !isNaN(averageSpeed)
        ? Number(averageSpeed)
        : null,
    };
  }

  // If period data is an array of time-series points, we would need to aggregate
  // But the API should provide summary data, so this is a fallback
  if (Array.isArray(periodData)) {
    console.warn('Period data is an array - expected object with summary. Attempting to use first item if it has summary structure.');
    if (periodData.length > 0 && typeof periodData[0] === 'object') {
      // Try to use first item if it looks like a summary
      const firstItem = periodData[0];
      if (firstItem.moving_time !== undefined || firstItem.ignition_time !== undefined) {
        return {
          movingTime: firstItem.moving_time ? msToTimeString(firstItem.moving_time) : null,
          pauseTime: firstItem.ideal_time ? msToTimeString(firstItem.ideal_time) : null,
          ignitionTime: firstItem.ignition_time ? msToTimeString(firstItem.ignition_time) : null,
          stopTime: firstItem.ignition_time && firstItem.moving_time
            ? msToTimeString(firstItem.ignition_time - firstItem.moving_time)
            : null,
          distanceTravelled: firstItem.distance_travelled || null,
          fuelConsumed: firstItem.fuel_consumed || null,
          possibleRefuel: firstItem.total_refuel ? Math.abs(firstItem.total_refuel) : null,
          possibleDrain: firstItem.total_theft || null,
          avgFuelLtrPerHr: firstItem.ignition_time && firstItem.fuel_consumed
            ? ((firstItem.fuel_consumed / (firstItem.ignition_time / (1000 * 60 * 60))).toFixed(2))
            : null,
          averageSpeed: firstItem.average_speed || firstItem.avg_speed || null,
        };
      }
    }
    
    // If array doesn't have summary structure, return nulls
    return {
      movingTime: null,
      pauseTime: null,
      ignitionTime: null,
      stopTime: null,
      distanceTravelled: null,
      fuelConsumed: null,
      possibleRefuel: null,
      possibleDrain: null,
      avgFuelLtrPerHr: null,
      averageSpeed: null,
    };
  }

  return {
    movingTime: null,
    pauseTime: null,
    ignitionTime: null,
    stopTime: null,
    distanceTravelled: null,
    fuelConsumed: null,
    possibleRefuel: null,
    possibleDrain: null,
    avgFuelLtrPerHr: null,
    averageSpeed: null,
  };
};

/**
 * Transform combined IoT data to match the expected UI format
 * @param {Object} combinedData - Combined data from fetchCombinedIotData
 * @param {moment.Moment} startMoment - Start date/time
 * @param {moment.Moment} endMoment - End date/time
 * @returns {Object} Transformed data matching mockIotData structure
 */
export const transformIotDataForUI = (combinedData, startMoment, endMoment) => {
  console.log('Transforming IoT data for UI:', {
    hasCombinedData: !!combinedData,
    hasDailySummaries: !!combinedData?.dailySummaries,
    dailySummariesLength: combinedData?.dailySummaries?.length || 0,
    hasPeriodData: !!combinedData?.periodData,
    periodDataLength: combinedData?.periodData?.length || 0,
    combinedDataKeys: combinedData ? Object.keys(combinedData) : [],
  });

  if (!combinedData) {
    console.error('No combined data provided');
    return null;
  }

  // Allow transformation even if one of the arrays is empty
  // (e.g., if we only have daily summaries or only period data)
  if (!combinedData.dailySummaries && !combinedData.periodData) {
    console.error('No daily summaries or period data available');
    return null;
  }

  // Initialize arrays if they don't exist
  const dailySummaries = combinedData.dailySummaries || [];
  const periodData = combinedData.periodData || [];

  // Calculate period statistics from all data
  let totalMovingTime = 0;
  let totalIgnitionTime = 0;
  let totalIdleTime = 0;
  let totalFuelConsumed = 0;
  let totalRefuel = 0;
  let totalTheft = 0;
  let totalDistance = 0;

  // Process daily summaries
  // Daily summaries might also have deviceProcessedData structure
  console.log(`Processing ${dailySummaries.length} daily summaries`);
  dailySummaries.forEach((summary, index) => {
    // Check if summary has deviceProcessedData structure
    const processed = summary.deviceProcessedData || summary;
    const newConsumed = processed.newConsumed || summary.newConsumed || {};
    
    // Check all possible locations for refuel and theft
    // Priority: summary root level > processed > newConsumed
    const refuelFromSummary = summary.total_refuel;
    const refuelFromProcessed = processed.total_refuel;
    const refuelFromNewConsumed = newConsumed.total_refuel;
    const theftFromSummary = summary.total_theft;
    const theftFromProcessed = processed.total_theft;
    const theftFromNewConsumed = newConsumed.total_theft;
    
    // Extract and parse date properly - priority: date_timestamp > date field
    // date_timestamp is in GMT/UTC, convert to IST (GMT+5:30) for the actual date
    let parsedDate = null;
    let dateString = null;
    
    // Priority 1: Use date_timestamp (in GMT/UTC) and convert to IST (GMT+5:30)
    const dateTimestamp = summary.date_timestamp || processed.date_timestamp;
    if (dateTimestamp) {
      // Parse timestamp as UTC, then convert to IST (GMT+5:30)
      parsedDate = moment.utc(dateTimestamp);
      if (parsedDate.isValid()) {
        // Convert to IST by adding 5 hours 30 minutes offset
        const istMoment = parsedDate.clone().utcOffset('+05:30');
        dateString = istMoment.format('YYYY-MM-DD');
      }
    }
    
    // Priority 2: Fallback to other date fields
    if (!dateString) {
      const rawDate = summary.date || processed.date || summary._date || processed._date;
      
      // Log all possible date-related fields
      const allDateFields = {
        'summary.date_timestamp': summary.date_timestamp,
        'processed.date_timestamp': processed.date_timestamp,
        'summary.date': summary.date,
        'processed.date': processed.date,
        'summary._date': summary._date,
        'processed._date': processed._date,
      };
      
      if (rawDate) {
        // Parse as UTC, then convert to IST
        if (typeof rawDate === 'string') {
          parsedDate = moment.utc(rawDate);
          if (!parsedDate.isValid()) {
            parsedDate = moment(rawDate);
          }
        } else if (typeof rawDate === 'number') {
          parsedDate = moment.utc(rawDate);
        } else if (rawDate instanceof Date) {
          parsedDate = moment.utc(rawDate);
        } else {
          parsedDate = moment.utc(rawDate);
        }
        
        // Convert to IST and format
        if (parsedDate.isValid()) {
          const istMoment = parsedDate.clone().utcOffset('+05:30');
          dateString = istMoment.format('YYYY-MM-DD');
        }
      }
    }
    
    console.log(`Daily summary ${index}:`, {
      date_timestamp: summary.date_timestamp || processed.date_timestamp,
      dateString: dateString,
      parsedDate: parsedDate ? parsedDate.format('YYYY-MM-DD HH:mm:ss') + ' UTC -> ' + parsedDate.clone().utcOffset('+05:30').format('YYYY-MM-DD HH:mm:ss') + ' IST' : 'invalid',
      hasDeviceProcessedData: !!summary.deviceProcessedData,
      moving_time: processed.moving_time,
      ignition_time: processed.ignition_time,
      fuel_consumed_processed: processed.fuel_consumed,
      fuel_consumed_newConsumed: newConsumed.fuel_consumed,
      // Refuel locations (priority order)
      refuel_from_summary_root: refuelFromSummary,
      refuel_from_processed: refuelFromProcessed,
      refuel_from_newConsumed: refuelFromNewConsumed,
      // Theft locations (priority order)
      theft_from_summary_root: theftFromSummary,
      theft_from_processed: theftFromProcessed,
      theft_from_newConsumed: theftFromNewConsumed,
      newConsumed: newConsumed,
      processedKeys: Object.keys(processed),
      summaryKeys: Object.keys(summary),
    });
    
    // Extract from deviceProcessedData or direct fields
    totalMovingTime += processed.moving_time || 0;
    totalIgnitionTime += processed.ignition_time || 0;
    totalIdleTime += processed.ideal_time || 0;
    // Fuel consumed might be in newConsumed.fuel_consumed or processed.fuel_consumed
    const fuelConsumed = newConsumed.fuel_consumed || processed.fuel_consumed || 0;
    totalFuelConsumed += fuelConsumed;
    // Get refuel from summary root level first (this is where the actual values are: 247.15... and 183.57...)
    // Priority: summary root > processed > newConsumed
    const totalRefuelValue = refuelFromSummary || refuelFromProcessed || refuelFromNewConsumed || 0;
    totalRefuel += totalRefuelValue ? Math.abs(totalRefuelValue) : 0;
    // Get theft from summary root level first
    const totalTheftValue = theftFromSummary || theftFromProcessed || theftFromNewConsumed || 0;
    totalTheft += totalTheftValue || 0;
    // Convert distance from meters to kilometers
    const distanceMeters = processed.distance_travelled || 0;
    totalDistance += distanceMeters / 1000; // Convert meters to km
  });

  // Process period data (partial days)
  console.log(`Processing ${periodData.length} period data entries`);
  periodData.forEach((period, index) => {
    console.log(`Period data ${index}:`, {
      date: period.date,
      hasData: !!period.data,
      dataType: Array.isArray(period.data) ? 'array' : typeof period.data,
      dataKeys: period.data && typeof period.data === 'object' ? Object.keys(period.data) : 'N/A',
      hasDeviceProcessedData: period.data && period.data.deviceProcessedData ? true : false,
    });
    
    // Calculate total duration for stop time calculation
    const totalDuration = period.endTime && period.startTime 
      ? period.endTime - period.startTime 
      : null;
    
    const periodStats = calculatePeriodStats(period.data, totalDuration);
    console.log(`Period stats calculated for ${period.date}:`, periodStats);
    
    // Add to totals if we have valid data
    if (periodStats.movingTime) {
      const movingMs = timeStringToMs(periodStats.movingTime);
      if (movingMs) totalMovingTime += movingMs;
    }
    if (periodStats.ignitionTime) {
      const ignitionMs = timeStringToMs(periodStats.ignitionTime);
      if (ignitionMs) totalIgnitionTime += ignitionMs;
    }
    if (periodStats.pauseTime) {
      const pauseMs = timeStringToMs(periodStats.pauseTime);
      if (pauseMs) totalIdleTime += pauseMs;
    }
    if (periodStats.fuelConsumed !== null) {
      totalFuelConsumed += periodStats.fuelConsumed;
    }
    if (periodStats.possibleRefuel !== null) {
      totalRefuel += periodStats.possibleRefuel;
    }
    if (periodStats.possibleDrain !== null) {
      totalTheft += periodStats.possibleDrain;
    }
    if (periodStats.distanceTravelled !== null) {
      totalDistance += periodStats.distanceTravelled;
    }
  });

  // Calculate hours run from meter readings if available
  let hoursRun = null;
  let firstMeter = null;
  let lastMeter = null;

  // Get first meter from first day (period or summary)
  if (periodData.find(d => d.isFirstDay)) {
    const firstDay = periodData.find(d => d.isFirstDay);
    // Try to get meter from period data summary
    if (firstDay.data && typeof firstDay.data === 'object') {
      const summary = firstDay.data.summary || firstDay.data;
      firstMeter = summary.start_meter || summary.meter_reading_start || null;
    }
  } else if (dailySummaries.length > 0) {
    firstMeter = dailySummaries[0].start_meter || dailySummaries[0].meter_reading_start || null;
  }

  // Get last meter from last day (period or summary)
  if (periodData.find(d => d.isLastDay)) {
    const lastDay = periodData.find(d => d.isLastDay);
    // Try to get meter from period data summary
    if (lastDay.data && typeof lastDay.data === 'object') {
      const summary = lastDay.data.summary || lastDay.data;
      lastMeter = summary.end_meter || summary.meter_reading_end || null;
    }
  } else if (dailySummaries.length > 0) {
    lastMeter = dailySummaries[dailySummaries.length - 1].end_meter || dailySummaries[dailySummaries.length - 1].meter_reading_end || null;
  }

  if (firstMeter !== null && lastMeter !== null) {
    hoursRun = lastMeter - firstMeter;
  }

  // Calculate average fuel consumption rate
  const totalIgnitionHours = totalIgnitionTime / (1000 * 60 * 60); // Convert ms to hours
  const avgFuelLtrPerHr = totalIgnitionHours > 0 ? totalFuelConsumed / totalIgnitionHours : null;
  const totalMovingHours = totalMovingTime / (1000 * 60 * 60);
  const averageSpeed = totalMovingHours > 0 && totalDistance > 0 ? (totalDistance / totalMovingHours) : null;

  // Build day-wise data
  const dayWiseData = [];

  // Add first day if partial
  if (periodData.find(d => d.isFirstDay)) {
    const firstDayData = periodData.find(d => d.isFirstDay);
    const firstDayDuration = firstDayData.endTime && firstDayData.startTime 
      ? firstDayData.endTime - firstDayData.startTime 
      : null;
    const periodStats = calculatePeriodStats(firstDayData.data, firstDayDuration);
    dayWiseData.push({
      date: firstDayData.date,
      isFullDay: false,
      dataSource: 'iot_table',
      movingTime: periodStats.movingTime,
      pauseTime: periodStats.pauseTime,
      ignitionTime: periodStats.ignitionTime,
      stopTime: periodStats.stopTime,
      distanceTravelled: periodStats.distanceTravelled,
      fuelConsumed: periodStats.fuelConsumed,
      possibleRefuel: periodStats.possibleRefuel,
      possibleDrain: periodStats.possibleDrain,
      avgFuelLtrPerHr: periodStats.avgFuelLtrPerHr,
      avgFuelKmPerL: periodStats.avgFuelKmPerL,
      averageSpeed: periodStats.averageSpeed,
    });
  }

  // Add full days
  dailySummaries.forEach((summary, dayIndex) => {
    // Check if summary has deviceProcessedData structure
    const processed = summary.deviceProcessedData || summary;
    const newConsumed = processed.newConsumed || summary.newConsumed || {};
    
    const movingTime = processed.moving_time || 0;
    const idealTime = processed.ideal_time || 0;
    const ignitionTime = processed.ignition_time || 0;
    // Convert distance from meters to kilometers
    const distanceTravelledMeters = processed.distance_travelled || 0;
    const distanceTravelled = distanceTravelledMeters / 1000; // Convert meters to km
    // Fuel consumed might be in newConsumed.fuel_consumed or processed.fuel_consumed
    const fuelConsumed = newConsumed.fuel_consumed || processed.fuel_consumed || 0;
    // Get refuel from summary root level first (this is where the actual values are: 247.15... and 183.57...)
    // Priority: summary root > processed > newConsumed
    const totalRefuelValue = summary.total_refuel || processed.total_refuel || newConsumed.total_refuel || 0;
    const possibleRefuel = totalRefuelValue ? Math.abs(totalRefuelValue) : 0;
    // Get theft from summary root level first
    const totalTheftValue = summary.total_theft || processed.total_theft || newConsumed.total_theft || 0;
    const possibleDrain = totalTheftValue || 0;
    
    // Calculate stop time: For full days, we don't have total duration, so use ignition - moving - ideal
    const stopTime = ignitionTime > 0 ? ignitionTime - movingTime - idealTime : 0;
    
    // Calculate averages
    const ignitionHours = ignitionTime / (1000 * 60 * 60);
    const avgFuelLtrPerHr = ignitionHours > 0 && fuelConsumed > 0
      ? (fuelConsumed / ignitionHours).toFixed(2)
      : null;
    const avgFuelKmPerL = fuelConsumed > 0 && distanceTravelled > 0
      ? (distanceTravelled / fuelConsumed).toFixed(2)
      : null;
    const movingHours = movingTime / (1000 * 60 * 60);
    const avgSpeedFromSummary = processed.average_speed ?? processed.avg_speed;
    const averageSpeed = avgSpeedFromSummary !== undefined && avgSpeedFromSummary !== null && !isNaN(avgSpeedFromSummary)
      ? Number(avgSpeedFromSummary)
      : ((movingHours > 0 && distanceTravelled > 0) ? (distanceTravelled / movingHours) : null);
    
    // Extract and parse date properly - use assigned date if available (from date matching above)
    // The assigned date should already be in IST (GMT+5:30) format
    let dayDateString = summary._assignedDate || summary.date;
    
    // If we have an assigned date (from date matching), use it directly
    if (dayDateString && typeof dayDateString === 'string' && dayDateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Already in YYYY-MM-DD format (IST), use it as-is
      console.log(`Using assigned date for summary ${dayIndex}:`, dayDateString);
    } else {
      // Try to parse from date_timestamp or other date fields
      // Priority: date_timestamp > date field
      const dateTimestamp = summary.date_timestamp || processed.date_timestamp;
      const dayRawDate = dateTimestamp || summary.date || processed.date || summary._date || processed._date;
      let dayParsedDate = null;
      
      console.log(`Building dayWiseData for summary ${dayIndex}:`, {
        assignedDate: summary._assignedDate,
        date_timestamp: dateTimestamp,
        rawDate: dayRawDate,
        dateType: typeof dayRawDate,
        summaryKeys: Object.keys(summary),
        processedKeys: Object.keys(processed),
      });
      
      if (dateTimestamp) {
        // Use date_timestamp: parse as UTC, then convert to IST (GMT+5:30)
        dayParsedDate = moment.utc(dateTimestamp);
        if (dayParsedDate.isValid()) {
          // Convert to IST by adding 5 hours 30 minutes offset
          const istMoment = dayParsedDate.clone().utcOffset('+05:30');
          dayDateString = istMoment.format('YYYY-MM-DD');
          console.log(`Date from timestamp for summary ${dayIndex}:`, {
            timestamp: dateTimestamp,
            utcDate: dayParsedDate.format('YYYY-MM-DD HH:mm:ss') + ' UTC',
            istDate: istMoment.format('YYYY-MM-DD HH:mm:ss') + ' IST',
            finalDate: dayDateString,
          });
        }
      } else if (dayRawDate) {
        // Fallback: parse other date fields as UTC, then convert to IST
        if (typeof dayRawDate === 'string') {
          dayParsedDate = moment.utc(dayRawDate);
          if (!dayParsedDate.isValid()) {
            dayParsedDate = moment(dayRawDate);
          }
        } else if (typeof dayRawDate === 'number') {
          dayParsedDate = moment.utc(dayRawDate);
        } else if (dayRawDate instanceof Date) {
          dayParsedDate = moment.utc(dayRawDate);
        } else {
          dayParsedDate = moment.utc(dayRawDate);
        }
        
        if (dayParsedDate.isValid()) {
          // Convert to IST
          const istMoment = dayParsedDate.clone().utcOffset('+05:30');
          dayDateString = istMoment.format('YYYY-MM-DD');
        }
        
        console.log(`Date parsing result for summary ${dayIndex}:`, {
          rawDate: dayRawDate,
          parsedDate: dayParsedDate.isValid() ? dayParsedDate.format('YYYY-MM-DD HH:mm:ss') + ' UTC' : 'invalid',
          istDate: dayParsedDate.isValid() ? dayParsedDate.clone().utcOffset('+05:30').format('YYYY-MM-DD HH:mm:ss') + ' IST' : 'invalid',
          dayDateString: dayDateString,
        });
      }
    }
    
    // Final check - if still no date, skip this entry
    if (!dayDateString) {
      console.error(`Skipping daily summary ${dayIndex} - no valid date found.`, {
        assignedDate: summary._assignedDate,
        date_timestamp: summary.date_timestamp || processed.date_timestamp,
        summaryDate: summary.date,
        processedDate: processed.date,
      });
      return;
    }
    
    dayWiseData.push({
      date: dayDateString,
      isFullDay: true,
      dataSource: 'day_summary',
      movingTime: movingTime > 0 ? msToTimeString(movingTime) : null,
      pauseTime: idealTime > 0 ? msToTimeString(idealTime) : null,
      ignitionTime: ignitionTime > 0 ? msToTimeString(ignitionTime) : null,
      stopTime: stopTime > 0 ? msToTimeString(stopTime) : null,
      distanceTravelled: distanceTravelled > 0 ? distanceTravelled : null,
      fuelConsumed: fuelConsumed > 0 ? fuelConsumed : null,
      possibleRefuel: possibleRefuel > 0 ? possibleRefuel : null,
      possibleDrain: possibleDrain > 0 ? possibleDrain : null,
      avgFuelLtrPerHr: avgFuelLtrPerHr,
      avgFuelKmPerL: avgFuelKmPerL,
      averageSpeed: averageSpeed,
    });
  });

  // Add last day if partial
  if (periodData.find(d => d.isLastDay)) {
    const lastDayData = periodData.find(d => d.isLastDay);
    const lastDayDuration = lastDayData.endTime && lastDayData.startTime 
      ? lastDayData.endTime - lastDayData.startTime 
      : null;
    const periodStats = calculatePeriodStats(lastDayData.data, lastDayDuration);
    dayWiseData.push({
      date: lastDayData.date,
      isFullDay: false,
      dataSource: 'iot_table',
      movingTime: periodStats.movingTime,
      pauseTime: periodStats.pauseTime,
      ignitionTime: periodStats.ignitionTime,
      stopTime: periodStats.stopTime,
      distanceTravelled: periodStats.distanceTravelled,
      fuelConsumed: periodStats.fuelConsumed,
      possibleRefuel: periodStats.possibleRefuel,
      possibleDrain: periodStats.possibleDrain,
      avgFuelLtrPerHr: periodStats.avgFuelLtrPerHr,
      avgFuelKmPerL: periodStats.avgFuelKmPerL,
      averageSpeed: periodStats.averageSpeed,
    });
  }

  return {
    iotPeriodStats: {
      totalMovingTime: msToTimeString(totalMovingTime),
      totalMovingTimeDIN: msToTimeString(totalMovingTime),
      totalPauseTime: msToTimeString(totalIdleTime),
      totalPauseTimeDIN: msToTimeString(totalIdleTime),
      totalIgnitionTime: msToTimeString(totalIgnitionTime),
      totalIgnitionTimeDIN: msToTimeString(totalIgnitionTime),
      totalStopTime: msToTimeString(totalIgnitionTime - totalMovingTime),
      totalStopTimeDIN: msToTimeString(totalIgnitionTime - totalMovingTime),
      distanceTravelled: totalDistance,
      fuelConsumed: totalFuelConsumed,
      possibleRefuel: totalRefuel,
      possibleDrain: totalTheft,
      avgFuelConsumedKmPerLtr: totalDistance > 0 && totalFuelConsumed > 0 
        ? (totalDistance / totalFuelConsumed).toFixed(2)
        : 0.0,
      avgFuelConsumedLtrPerHr: avgFuelLtrPerHr ? avgFuelLtrPerHr.toFixed(2) : null,
      avgFuelConsumedLtrPerHrDIN: avgFuelLtrPerHr ? avgFuelLtrPerHr.toFixed(2) : null,
      averageSpeed: averageSpeed !== null && averageSpeed !== undefined ? Number(averageSpeed.toFixed(2)) : null,
      kmsRun: null, // Will be populated if available
      kwhRun: null, // Will be populated if available
    },
    iotMetrics: {
      fuelAdded: totalRefuel,
      hoursRun: hoursRun,
      kmsRun: totalDistance > 0 ? totalDistance / 1000 : null, // Convert meters to km
      kwhRun: null,
    },
    dayWiseData: dayWiseData,
    // Keep existing refuel events and other data
    events: [],
    timeseries: [],
  };
};

/**
 * Find asset ID by asset code
 * @param {string} assetCode - Asset code (e.g., '20E-290')
 * @returns {Promise<{success: boolean, assetId: string|null, error: string|null}>}
 */
export const findAssetIdByCode = async (assetCode) => {
  try {
    console.log('Finding asset ID for code:', assetCode);
    
    // Asset API requires Accept: */* and no Content-Type header
    const result = await apiGet('/asset/asset-all', {
      headers: {
        'Accept': '*/*',
        // Explicitly remove Content-Type by not including it
      },
    });

    console.log('Asset API result:', result);

    if (!result.success) {
      console.error('Failed to fetch assets:', result.error);
      return { success: false, assetId: null, error: result.error };
    }

    const assets = result.data || [];
    const asset = assets.find(a => a.assetCode === assetCode);

    if (!asset) {
      return { 
        success: false, 
        assetId: null, 
        error: `Asset with code '${assetCode}' not found` 
      };
    }

    // Extract location and client IDs
    const locationId = asset.location;
    const clientId = asset.client; // client is the subLocation

    // Fetch location name if location ID exists
    let locationName = null;
    if (locationId) {
      try {
        // Check if locationId is already a populated object with name
        if (typeof locationId === 'object' && locationId.name) {
          locationName = locationId.name;
        } else if (typeof locationId === 'string') {
          // It's an ID, fetch the location details
          const locationResult = await apiGet(`/location/${locationId}`, {
            headers: {
              'Accept': '*/*',
            },
          });
          
          if (locationResult.success && locationResult.data) {
            locationName = locationResult.data.name || locationResult.data.code || locationResult.data.title || null;
          } else {
            console.warn(`Failed to fetch location for ID ${locationId}:`, locationResult.error);
          }
        }
      } catch (error) {
        console.error(`Error fetching location ${locationId}:`, error);
        // Continue without location name if API call fails
      }
    }

    // Fetch client (subLocation) name if client ID exists
    let subLocationName = null;
    if (clientId) {
      try {
        // Check if clientId is already a populated object with name
        if (typeof clientId === 'object' && clientId.name) {
          subLocationName = clientId.name;
        } else if (typeof clientId === 'string') {
          // It's an ID, fetch the client details
          const clientResult = await apiGet(`/client/${clientId}`, {
            headers: {
              'Accept': '*/*',
            },
          });
          
          if (clientResult.success && clientResult.data) {
            subLocationName = clientResult.data.name || clientResult.data.code || clientResult.data.title || null;
          } else {
            console.warn(`Failed to fetch client for ID ${clientId}:`, clientResult.error);
          }
        }
      } catch (error) {
        console.error(`Error fetching client ${clientId}:`, error);
        // Continue without subLocation name if API call fails
      }
    }

    return { 
      success: true, 
      assetId: asset._id, 
      asset,
      altName: asset.altName || null,
      ownerCode: asset.ownerCode || null,
      assetModel: asset.assetModel || null,
      location: locationName,
      subLocation: subLocationName,
      error: null 
    };
  } catch (error) {
    console.error('Error finding asset ID:', error);
    return { success: false, assetId: null, error: error.message };
  }
};

/**
 * Fetch fuel entries for a specific asset within a date range
 * @param {string} assetId - Asset ID
 * @param {string} periodFrom - Start date in ISO 8601 format
 * @param {string} periodTo - End date in ISO 8601 format
 * @returns {Promise<{success: boolean, data: Array, error: string|null}>}
 */
export const fetchFuelEntries = async (assetId, periodFrom, periodTo) => {
  try {
    const result = await apiPost('/fuel/mileage-report', {
      asset: assetId,
      location: 'all',
      subLocation: 'all',
      periodFrom: periodFrom,
      periodTo: periodTo,
    });

    if (!result.success) {
      return { success: false, data: [], error: result.error };
    }

    return { success: true, data: result.data || [], error: null };
  } catch (error) {
    console.error('Error fetching fuel entries:', error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Fetch all fuel entries (full tank and non-full tank) between last full tank and current full tank
 * @param {string} assetCode - Asset code (e.g., '20E-290')
 * @param {string} lastFullTankTime - Last full tank time in format 'YYYY-MM-DD HH:mm:ss'
 * @param {string} currentFullTankTime - Current full tank time in format 'YYYY-MM-DD HH:mm:ss'
 * @param {string} currentVoucherNo - Optional: Current refuel voucher number to ensure it's included
 * @returns {Promise<{success: boolean, entries: Array, error: string|null}>}
 */
export const fetchAllFuelEntriesBetweenFullTanks = async (assetCode, lastFullTankTime, currentFullTankTime, currentVoucherNo = null) => {
  try {
    console.log(`Fetching all fuel entries for asset ${assetCode} between ${lastFullTankTime} and ${currentFullTankTime}`);

    // Step 1: Find asset ID
    const assetResult = await findAssetIdByCode(assetCode);
    if (!assetResult.success) {
      return { success: false, entries: [], error: assetResult.error };
    }

    const assetId = assetResult.assetId;
    console.log(`Found asset ID: ${assetId}`);

    // Step 2: Convert dates to ISO format
    const lastFullTankMoment = moment(lastFullTankTime, 'YYYY-MM-DD HH:mm:ss');
    const currentFullTankMoment = moment(currentFullTankTime, 'YYYY-MM-DD HH:mm:ss');

    if (!lastFullTankMoment.isValid() || !currentFullTankMoment.isValid()) {
      return {
        success: false,
        entries: [],
        error: 'Invalid date format. Expected YYYY-MM-DD HH:mm:ss'
      };
    }

    // Step 3: Extend date range: 1 second before last full tank to 1 second after current refuel event
    // This ensures we capture all entries including the exact timestamps
    const startMoment = lastFullTankMoment.clone().subtract(1, 'second');
    const endMoment = currentFullTankMoment.clone().add(1, 'second');

    console.log(`Fetching entries from ${startMoment.format('YYYY-MM-DD HH:mm:ss')} to ${endMoment.format('YYYY-MM-DD HH:mm:ss')}`);

    // Step 4: Fetch all fuel entries in the period (including both full tank and non-full tank)
    const result = await fetchFuelEntries(
      assetId,
      startMoment.toISOString(),
      endMoment.toISOString()
    );

    if (!result.success) {
      return { success: false, entries: [], error: result.error };
    }

    // Step 5: Filter entries to include those from 1 second before last full tank to 1 second after current refuel
    // Include all entries in this range
    const allEntries = result.data
      .filter(entry => {
        const entryDate = moment(entry.date);
        // Include entries from start (1 sec before last full tank) to end (1 sec after current refuel)
        return entryDate.isSameOrAfter(startMoment) && entryDate.isSameOrBefore(endMoment);
      })
      .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()); // Sort ascending (oldest first)

    console.log(`Filtered ${allEntries.length} entries from ${startMoment.format('YYYY-MM-DD HH:mm:ss')} to ${endMoment.format('YYYY-MM-DD HH:mm:ss')}`);
    
    // Log all entry dates for debugging
    console.log('Entry dates:', allEntries.map(e => `${moment(e.date).format('YYYY-MM-DD HH:mm:ss')} (${e.voucherNo || 'N/A'})`));
    
    // Log if current refuel event is found
    const currentRefuelFound = allEntries.some(entry => {
      const entryDate = moment(entry.date);
      // Check if entry is within 5 seconds of current refuel time
      return Math.abs(entryDate.diff(currentFullTankMoment, 'seconds')) <= 5;
    });
    
    // If current refuel event not found and we have a voucher number, try to find it by voucher
    if (!currentRefuelFound && currentVoucherNo) {
      const foundByVoucher = allEntries.find(entry => entry.voucherNo === currentVoucherNo);
      if (foundByVoucher) {
        console.log('Current refuel event found by voucher number:', currentVoucherNo);
      } else {
        console.warn('Current refuel event not found even by voucher number. Current refuel time:', currentFullTankTime, 'Voucher:', currentVoucherNo);
        console.warn('All entry dates:', allEntries.map(e => `${moment(e.date).format('YYYY-MM-DD HH:mm:ss')} (${e.voucherNo || 'N/A'})`));
      }
    } else if (currentRefuelFound) {
      console.log('Current refuel event found in entries');
    }

    console.log(`Found ${allEntries.length} fuel entries between full tanks`);

    return {
      success: true,
      entries: allEntries,
      error: null
    };
  } catch (error) {
    console.error('Error fetching all fuel entries:', error);
    return {
      success: false,
      entries: [],
      error: error.message
    };
  }
};

/**
 * Fetch IoT sensor detected refuel events
 * @param {string} assetCode - Asset code (e.g., '20E-290')
 * @param {string} currentRefuelTime - Current refuel time in format 'YYYY-MM-DD HH:mm:ss'
 * @param {string} lastFullTankTime - Last full tank time in format 'YYYY-MM-DD HH:mm:ss' (optional)
 * @returns {Promise<{success: boolean, events: Array, error: string|null}>}
 */
/**
 * Helper function to fetch refuel events for a single date range
 * @param {string} assetId - Asset ID
 * @param {string} assetCode - Asset code (for verification)
 * @param {number} startTimestamp - Start timestamp in milliseconds (UTC)
 * @param {number} endTimestamp - End timestamp in milliseconds (UTC)
 * @param {string} label - Label for logging (e.g., "Last Full Tank" or "Current Full Tank")
 * @returns {Promise<{success: boolean, events: Array, error: string|null}>}
 */
const fetchRefuelEventsForRange = async (assetId, assetCode, startTimestamp, endTimestamp, label) => {
  try {
    const endpoint = `/fuel/asset/${assetId}/refuel-data/${startTimestamp}/${endTimestamp}`;
    console.log(`[${label}] Fetching from API: ${endpoint}`);
    
    const result = await apiGet(endpoint);

    if (!result.success) {
      console.error(`[${label}] API call failed:`, result.error);
      return { success: false, events: [], error: result.error };
    }

    // Extract refuel events from the API response structure
    let refuelEvents = [];
    
    if (result.data) {
      // Check if response includes asset information (for verification)
      if (result.data.asset) {
        const responseAssetId = result.data.asset._id;
        const responseAssetCode = result.data.asset.assetCode;
        console.log(`[${label}] Response includes asset info - ID: ${responseAssetId}, Code: ${responseAssetCode}`);
        
        if (responseAssetId !== assetId) {
          console.warn(`[${label}] WARNING: Response asset ID (${responseAssetId}) does not match requested asset ID (${assetId})`);
        }
        if (responseAssetCode !== assetCode) {
          console.warn(`[${label}] WARNING: Response asset code (${responseAssetCode}) does not match requested asset code (${assetCode})`);
        }
      }
      
      // PRIORITY: Check if response is an array of daily summaries FIRST (need to filter by asset)
      if (Array.isArray(result.data) && result.data.length > 0) {
        if (result.data[0].deviceProcessedData || result.data[0].asset) {
          refuelEvents = [];
          result.data.forEach((summary, index) => {
            // CRITICAL: Verify asset matches
            if (summary.asset) {
              const summaryAssetId = summary.asset._id;
              const summaryAssetCode = summary.asset.assetCode;
              
              if (summaryAssetId !== assetId || summaryAssetCode !== assetCode) {
                console.warn(`[${label}] Skipping summary ${index} - asset mismatch`);
                return;
              }
            }
            
            // Extract refuel events from this summary
            if (summary.deviceProcessedData && summary.deviceProcessedData.refuel_events) {
              refuelEvents.push(...summary.deviceProcessedData.refuel_events);
            } else if (summary.deviceProcessedData && 
                       summary.deviceProcessedData.newConsumed && 
                       summary.deviceProcessedData.newConsumed.refuel_events) {
              refuelEvents.push(...summary.deviceProcessedData.newConsumed.refuel_events);
            }
          });
          console.log(`[${label}] Extracted ${refuelEvents.length} refuel events from ${result.data.length} daily summaries`);
        } else {
          refuelEvents = result.data;
          console.log(`[${label}] Response is array format, found ${refuelEvents.length} events`);
        }
      }
      // Single summary with deviceProcessedData
      else if (result.data.deviceProcessedData && result.data.deviceProcessedData.refuel_events) {
        refuelEvents = result.data.deviceProcessedData.refuel_events;
        console.log(`[${label}] Found ${refuelEvents.length} refuel events in deviceProcessedData.refuel_events`);
      } 
      // Nested in newConsumed
      else if (result.data.deviceProcessedData && 
               result.data.deviceProcessedData.newConsumed && 
               result.data.deviceProcessedData.newConsumed.refuel_events) {
        refuelEvents = result.data.deviceProcessedData.newConsumed.refuel_events;
        console.log(`[${label}] Found ${refuelEvents.length} refuel events in deviceProcessedData.newConsumed.refuel_events`);
      }
      // Root level refuel_events
      else if (result.data.refuel_events && Array.isArray(result.data.refuel_events)) {
        refuelEvents = result.data.refuel_events;
        console.log(`[${label}] Found ${refuelEvents.length} refuel events at root level`);
      }
    }
    
    console.log(`[${label}] Final count: ${refuelEvents.length} refuel events`);
    return { success: true, events: refuelEvents, error: null };
  } catch (error) {
    console.error(`[${label}] Error:`, error);
    return { success: false, events: [], error: error.message };
  }
};

/**
 * Helper function to fetch 3 days of data for a fuel entry date (API only supports 24-hour ranges)
 * Makes 3 API calls: day before, same day, day after
 * @param {string} assetId - Asset ID
 * @param {string} assetCode - Asset code (for verification)
 * @param {moment} fuelEntryMoment - Moment object for the fuel entry date (in IST)
 * @param {string} label - Label for logging (e.g., "Last Full Tank" or "Current Full Tank")
 * @returns {Promise<Array>} Array of refuel events
 */
const fetch3DaysOfRefuelEvents = async (assetId, assetCode, fuelEntryMoment, label) => {
  const events = [];
  
  // Day before: (date-1) 00:00:00 IST to (date-1) 23:59:59 IST
  const dayBeforeMoment = fuelEntryMoment.clone().subtract(1, 'day');
  const dayBeforeStart = dayBeforeMoment.clone().startOf('day');
  const dayBeforeEnd = dayBeforeMoment.clone().endOf('day');
  const dayBeforeStartTs = dayBeforeStart.clone().utc().valueOf();
  const dayBeforeEndTs = dayBeforeEnd.clone().utc().valueOf();
  
  console.log(`\n[${label}] Day Before: ${dayBeforeStart.format('YYYY-MM-DD HH:mm:ss')} to ${dayBeforeEnd.format('YYYY-MM-DD HH:mm:ss')} IST`);
  console.log(`[${label}] Day Before Timestamps: ${dayBeforeStartTs} to ${dayBeforeEndTs}`);
  
  const dayBeforeResult = await fetchRefuelEventsForRange(assetId, assetCode, dayBeforeStartTs, dayBeforeEndTs, `${label} - Day Before`);
  if (dayBeforeResult.success && dayBeforeResult.events.length > 0) {
    events.push(...dayBeforeResult.events);
  }
  
  // Same day: (date) 00:00:00 IST to (date) 23:59:59 IST
  const sameDayStart = fuelEntryMoment.clone().startOf('day');
  const sameDayEnd = fuelEntryMoment.clone().endOf('day');
  const sameDayStartTs = sameDayStart.clone().utc().valueOf();
  const sameDayEndTs = sameDayEnd.clone().utc().valueOf();
  
  console.log(`[${label}] Same Day: ${sameDayStart.format('YYYY-MM-DD HH:mm:ss')} to ${sameDayEnd.format('YYYY-MM-DD HH:mm:ss')} IST`);
  console.log(`[${label}] Same Day Timestamps: ${sameDayStartTs} to ${sameDayEndTs}`);
  
  const sameDayResult = await fetchRefuelEventsForRange(assetId, assetCode, sameDayStartTs, sameDayEndTs, `${label} - Same Day`);
  if (sameDayResult.success && sameDayResult.events.length > 0) {
    events.push(...sameDayResult.events);
  }
  
  // Day after: (date+1) 00:00:00 IST to (date+1) 23:59:59 IST
  const dayAfterMoment = fuelEntryMoment.clone().add(1, 'day');
  const dayAfterStart = dayAfterMoment.clone().startOf('day');
  const dayAfterEnd = dayAfterMoment.clone().endOf('day');
  const dayAfterStartTs = dayAfterStart.clone().utc().valueOf();
  const dayAfterEndTs = dayAfterEnd.clone().utc().valueOf();
  
  console.log(`[${label}] Day After: ${dayAfterStart.format('YYYY-MM-DD HH:mm:ss')} to ${dayAfterEnd.format('YYYY-MM-DD HH:mm:ss')} IST`);
  console.log(`[${label}] Day After Timestamps: ${dayAfterStartTs} to ${dayAfterEndTs}`);
  
  const dayAfterResult = await fetchRefuelEventsForRange(assetId, assetCode, dayAfterStartTs, dayAfterEndTs, `${label} - Day After`);
  if (dayAfterResult.success && dayAfterResult.events.length > 0) {
    events.push(...dayAfterResult.events);
  }
  
  console.log(`[${label}] Total events from 3 days: ${events.length}`);
  return events;
};

export const fetchIotRefuelEvents = async (assetCode, currentRefuelTime, lastFullTankTime = null, providedAssetId = null) => {
  try {
    console.log(`Fetching IoT refuel events for asset ${assetCode}`);
    console.log(`  Current Full Tank: ${currentRefuelTime}`);
    console.log(`  Last Full Tank: ${lastFullTankTime || 'N/A'}`);

    // Step 1: Get asset ID - use provided ID if available, otherwise look it up
    let assetId = providedAssetId;
    if (!assetId) {
      console.log('No asset ID provided, looking up via asset-all API...');
      const assetResult = await findAssetIdByCode(assetCode);
      if (!assetResult.success) {
        return { success: false, events: [], error: assetResult.error };
      }
      assetId = assetResult.assetId;
    } else {
      console.log(`Using provided asset ID: ${assetId} (skipping asset-all API call)`);
    }

    // Step 2: Validate input times
    const currentRefuelMoment = moment(currentRefuelTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
    if (!currentRefuelMoment.isValid()) {
      return {
        success: false,
        events: [],
        error: 'Invalid currentRefuelTime format. Expected YYYY-MM-DD HH:mm:ss'
      };
    }

    // Step 3: Make API calls - 3 calls per fuel entry date (API only supports 24-hour ranges)
    // For each fuel entry, fetch: day before, same day, day after
    // Example: If fuel entry is 12 Jan 2026 10:30 AM:
    //   - Day before: 11 Jan 2026 00:00:00 to 11 Jan 2026 23:59:59 IST
    //   - Same day: 12 Jan 2026 00:00:00 to 12 Jan 2026 23:59:59 IST
    //   - Day after: 13 Jan 2026 00:00:00 to 13 Jan 2026 23:59:59 IST
    
    let allRefuelEvents = [];
    
    // Fetch 3 days of data for Last Full Tank (if provided)
    if (lastFullTankTime) {
      const lastFullTankMoment = moment(lastFullTankTime, 'YYYY-MM-DD HH:mm:ss').utcOffset('+05:30');
      if (!lastFullTankMoment.isValid()) {
        return {
          success: false,
          events: [],
          error: 'Invalid lastFullTankTime format. Expected YYYY-MM-DD HH:mm:ss'
        };
      }
      
      console.log(`\n========== LAST FULL TANK: ${lastFullTankTime} IST ==========`);
      const lastFTEvents = await fetch3DaysOfRefuelEvents(assetId, assetCode, lastFullTankMoment, 'Last Full Tank');
      allRefuelEvents.push(...lastFTEvents);
    }
    
    // Fetch 3 days of data for Current Full Tank
    console.log(`\n========== CURRENT FULL TANK: ${currentRefuelTime} IST ==========`);
    const currentFTEvents = await fetch3DaysOfRefuelEvents(assetId, assetCode, currentRefuelMoment, 'Current Full Tank');
    allRefuelEvents.push(...currentFTEvents);
    
    // Step 4: Remove duplicate events (based on timestamp)
    // Events with the same timestamp are considered duplicates
    const uniqueEventsMap = new Map();
    allRefuelEvents.forEach(event => {
      const key = event.timestamp || event.date || JSON.stringify(event);
      if (!uniqueEventsMap.has(key)) {
        uniqueEventsMap.set(key, event);
      }
    });
    const uniqueEvents = Array.from(uniqueEventsMap.values());
    
    console.log(`\n========== SUMMARY ==========`);
    console.log(`[fetchIotRefuelEvents] Total API calls: ${lastFullTankTime ? 6 : 3}`);
    console.log(`[fetchIotRefuelEvents] Total events fetched: ${allRefuelEvents.length}`);
    console.log(`[fetchIotRefuelEvents] Unique events after deduplication: ${uniqueEvents.length}`);

    return {
      success: true,
      events: uniqueEvents,
      error: null
    };
  } catch (error) {
    console.error('Error fetching IoT refuel events:', error);
    return {
      success: false,
      events: [],
      error: error.message
    };
  }
};

/**
 * Transform IoT refuel event to display format
 * @param {Object} apiEvent - IoT refuel event from API
 * @param {number|null} manualQuantity - Manual refuel quantity for confidence calculation
 * @returns {Object} Transformed event for display
 */
export const transformIotRefuelEvent = (apiEvent, manualQuantity = null) => {
  // Handle new API format: apiEvent is a refuel event object with timestamp, refuelAmount, etc.
  // Handle old API format: apiEvent is a full fuel entry object with date, quantity, etc.
  
  let eventDate;
  let quantity = 0;
  let isFullTank = false;
  let voucherNo = '';
  let meterReading = null;
  let location = null;
  let photos = [];
  let timeSource = 'unknown';
  let eventId = null;
  
  // Check if this is the new API format (refuel event object from refuel_events array)
  if (apiEvent.timestamp && apiEvent.refuelAmount !== undefined) {
    // New API format: { index, timestamp, refuelAmount, axis_variance_previous, axis_variance_next }
    eventDate = moment(apiEvent.timestamp);
    quantity = Math.abs(apiEvent.refuelAmount || 0); // refuelAmount is negative, so take absolute value
    isFullTank = quantity > 100; // Heuristic: consider > 100L as full tank
    eventId = `iot-refuel-${apiEvent.index || apiEvent.timestamp}`;
    timeSource = 'timestamp';
    console.log(`New API format: Using timestamp ${apiEvent.timestamp} for refuel event, amount: ${quantity}L`);
  }
  // Old API format: full fuel entry object
  else if (apiEvent.date || apiEvent._id) {
    // Priority order for finding the refuel event start time:
    // 1. refuel_events[0].timestamp (actual sensor-detected start time in milliseconds)
    // 2. ids[0].date (if available)
    // 3. date (root level, might be end/completion time)
    
    if (apiEvent.refuel_events && Array.isArray(apiEvent.refuel_events) && apiEvent.refuel_events.length > 0) {
      // Use the first refuel event's timestamp (in milliseconds) - this is the actual start time
      const firstRefuelEvent = apiEvent.refuel_events[0];
      if (firstRefuelEvent.timestamp) {
        eventDate = moment(firstRefuelEvent.timestamp);
        timeSource = 'refuel_events[0].timestamp';
        quantity = Math.abs(firstRefuelEvent.refuelAmount || apiEvent.quantity || 0);
        console.log(`Using refuel_events[0].timestamp for refuel event ${apiEvent._id}: ${firstRefuelEvent.timestamp} (${eventDate.format('YYYY-MM-DD HH:mm:ss')})`);
      } else {
        // Fallback to ids array or main date
        if (apiEvent.ids && Array.isArray(apiEvent.ids) && apiEvent.ids.length > 0 && apiEvent.ids[0].date) {
          eventDate = moment(apiEvent.ids[0].date);
          timeSource = 'ids[0]';
          quantity = apiEvent.quantity || 0;
          console.log(`Using ids[0].date for refuel event ${apiEvent._id}: ${apiEvent.ids[0].date}`);
        } else {
          eventDate = moment(apiEvent.date);
          quantity = apiEvent.quantity || 0;
          console.log(`Using main date for refuel event ${apiEvent._id}: ${apiEvent.date}`);
        }
      }
    } else if (apiEvent.ids && Array.isArray(apiEvent.ids) && apiEvent.ids.length > 0) {
      // Fallback to ids array if refuel_events is not available
      const firstId = apiEvent.ids[0];
      if (firstId.date) {
        eventDate = moment(firstId.date);
        timeSource = 'ids[0]';
        quantity = apiEvent.quantity || 0;
        console.log(`Using ids[0].date for refuel event ${apiEvent._id}: ${firstId.date}`);
      } else {
        eventDate = moment(apiEvent.date);
        quantity = apiEvent.quantity || 0;
        console.log(`ids array exists but no date field, using main date for refuel event ${apiEvent._id}`);
      }
    } else {
      // Final fallback to root date field
      eventDate = moment(apiEvent.date);
      quantity = apiEvent.quantity || 0;
      console.log(`No refuel_events or ids array, using main date for refuel event ${apiEvent._id}: ${apiEvent.date}`);
    }
    
    // Extract other fields from old API format
    isFullTank = apiEvent.isFullTank || false;
    voucherNo = apiEvent.voucherNo || '';
    meterReading = apiEvent.mileage && apiEvent.mileage.length > 0 
      ? apiEvent.mileage[0].reading 
      : null;
    location = apiEvent.location ? (apiEvent.location.name || null) : null;
    photos = apiEvent.photos || [];
    eventId = apiEvent._id || `iot-${eventDate.format('YYYY-MM-DD-HH-mm-ss')}`;
  } else {
    // Unknown format, try to extract what we can
    console.warn('Unknown API event format:', apiEvent);
    eventDate = moment();
    eventId = `iot-unknown-${Date.now()}`;
  }
  
  const timeText = eventDate.format('YYYY-MM-DD HH:mm:ss');
  
  // Determine confidence based on quantity match with manual entry
  let confidence = 'medium';
  let quantityDifference = null;
  let quantityDifferencePercent = null;
  
  if (manualQuantity !== null && manualQuantity > 0 && quantity > 0) {
    // Calculate difference between IoT detected quantity and manual quantity
    quantityDifference = Math.abs(quantity - manualQuantity);
    quantityDifferencePercent = (quantityDifference / manualQuantity) * 100;
    
    // Confidence based on how close the quantities match:
    // High: within 5% or 10L (whichever is smaller)
    // Medium: within 15% or 30L
    // Low: anything else
    const percentThreshold = Math.min(5, (10 / manualQuantity) * 100);
    const absoluteThreshold = Math.min(10, manualQuantity * 0.05);
    
    if (quantityDifferencePercent <= percentThreshold || quantityDifference <= absoluteThreshold) {
      confidence = 'high';
    } else if (quantityDifferencePercent <= 15 || quantityDifference <= 30) {
      confidence = 'medium';
    } else {
      confidence = 'low';
    }
  } else {
    // Fallback to old logic if no manual quantity available
    if (isFullTank && quantity > 200) {
      confidence = 'high';
    } else if (quantity < 50) {
      confidence = 'low';
    }
  }

  return {
    id: eventId,
    timeText: timeText,
    timeMoment: eventDate,
    description: `Sensor detected refuel - ${quantity.toFixed(2)}L added${isFullTank ? ' (Full Tank)' : ''}`,
    confidence: confidence,
    relatedTo: null, // Will be determined based on date comparison
    quantity: quantity,
    isFullTank: isFullTank,
    voucherNo: voucherNo,
    meterReading: meterReading,
    location: location,
    photos: photos,
    manualQuantity: manualQuantity, // Manual entry quantity for comparison
    quantityDifference: quantityDifference, // Absolute difference in liters
    quantityDifferencePercent: quantityDifferencePercent, // Percentage difference
    // Include full API event for reference
    _apiEvent: apiEvent,
    _timeSource: timeSource, // Debug: track which time field was used
  };
};

/**
 * Transform API fuel entry to fuel entries table format
 * @param {Object} apiEntry - Fuel entry from API
 * @returns {Object} Transformed entry for table display
 */
export const transformFuelEntryForTable = (apiEntry) => {
  // Get all meter readings (KWH, HOURS, KM) when available
  let meterReading = null; // Primary meter reading (for backward compatibility)
  let hoursReading = null;
  let kwhReading = null;
  let kmReading = null;
  
  if (apiEntry.mileage && apiEntry.mileage.length > 0) {
    // Find all meter types
    const kwhMeter = apiEntry.mileage.find(m => 
      m.name && m.name.toUpperCase().includes('KWH')
    );
    const hoursMeter = apiEntry.mileage.find(m => 
      m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
    );
    const kmMeter = apiEntry.mileage.find(m => 
      m.name && (m.name.toUpperCase().includes('KM') || 
                m.name.toUpperCase().includes('KMS') || 
                m.name.toUpperCase().includes('ODO') ||
                m.name.toUpperCase().includes('DISTANCE'))
    );
    
    // Extract readings
    if (kwhMeter && kwhMeter.reading !== null && kwhMeter.reading !== undefined) {
      kwhReading = kwhMeter.reading;
    }
    if (hoursMeter && hoursMeter.reading !== null && hoursMeter.reading !== undefined) {
      hoursReading = hoursMeter.reading;
    }
    if (kmMeter && kmMeter.reading !== null && kmMeter.reading !== undefined) {
      kmReading = kmMeter.reading;
    }
    
    // Select primary meter based on priority: KWH > HOURS > KM > first available
    meterReading = kwhMeter ? kwhMeter.reading : 
                   (hoursMeter ? hoursMeter.reading : 
                   (kmMeter ? kmMeter.reading : apiEntry.mileage[0].reading));
  }

  // Format date and time
  const entryMoment = moment(apiEntry.date);
  const date = entryMoment.format('YYYY-MM-DD');
  const time = entryMoment.format('HH:mm:ss');
  const dateTime = entryMoment.format('YYYY-MM-DD HH:mm:ss');

  // Transform photos
  const photos = (apiEntry.photos || []).map((photo, index) => ({
    id: `photo-${index + 1}`,
    url: photo.url,
    thumbnail: photo.url, // Use same URL for thumbnail if no separate thumbnail
    name: photo.name,
    mimeType: photo.mimeType,
  }));

  return {
    id: apiEntry._id || `entry-${dateTime}`,
    date: date,
    time: time,
    dateTime: dateTime,
    voucherNo: apiEntry.voucherNo || '',
    meterReading: meterReading, // Primary meter reading (for backward compatibility)
    hoursReading: hoursReading, // Hours meter reading (when available)
    kwhReading: kwhReading, // KWH meter reading (when available)
    kmReading: kmReading, // KM meter reading (when available)
    quantity: apiEntry.quantity || 0,
    isFullTank: apiEntry.isFullTank || false,
    photos: photos,
    // Include full API entry for reference
    _apiEntry: apiEntry,
  };
};

/**
 * Fetch last N full tank events for an asset before a specific date
 * Fetches data in batches of 10 days going backwards until we have enough events
 * @param {string} assetCode - Asset code (e.g., '20E-290')
 * @param {string} beforeDate - Date string in format 'YYYY-MM-DD HH:mm:ss'
 * @param {number} count - Number of full tank events to fetch (default: 7)
 * @returns {Promise<{success: boolean, events: Array, error: string|null}>}
 */
export const fetchLastFullTankEvents = async (assetCode, beforeDate, count = 7) => {
  try {
    console.log(`Fetching last ${count} full tank events for asset ${assetCode} before ${beforeDate}`);

    // Step 1: Find asset ID
    const assetResult = await findAssetIdByCode(assetCode);
    if (!assetResult.success) {
      return { success: false, events: [], allEntries: [], error: assetResult.error };
    }

    const assetId = assetResult.assetId;
    console.log(`Found asset ID: ${assetId}`);

    // Step 2: Fetch fuel entries in batches
    const beforeMoment = moment(beforeDate, 'YYYY-MM-DD HH:mm:ss');
    if (!beforeMoment.isValid()) {
      return { 
        success: false, 
        events: [], 
        allEntries: [],
        error: `Invalid date format: ${beforeDate}. Expected 'YYYY-MM-DD HH:mm:ss'` 
      };
    }

    const fullTankEvents = [];
    const allFuelEntries = []; // Also collect ALL entries (full tank and non-full tank)
    let currentEndDate = beforeMoment.clone();
    const batchSizeDays = 10;
    const maxBatches = 20; // Safety limit to prevent infinite loops
    let batchCount = 0;

    while (fullTankEvents.length < count && batchCount < maxBatches) {
      // Calculate date range for this batch (10 days going backwards)
      const batchStartDate = currentEndDate.clone().subtract(batchSizeDays, 'days');
      
      console.log(`Fetching batch ${batchCount + 1}: ${batchStartDate.toISOString()} to ${currentEndDate.toISOString()}`);

      // Fetch fuel entries for this batch
      const result = await fetchFuelEntries(
        assetId,
        batchStartDate.toISOString(),
        currentEndDate.toISOString()
      );

      if (!result.success) {
        console.error(`Error fetching batch ${batchCount + 1}:`, result.error);
        // Continue with next batch instead of failing completely
        currentEndDate = batchStartDate;
        batchCount++;
        continue;
      }

      // Collect ALL entries from this batch (before the target date)
      const batchAllEntries = result.data
        .filter(entry => {
          const entryDate = moment(entry.date);
          return entryDate.isBefore(beforeMoment);
        })
        .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
      
      allFuelEntries.push(...batchAllEntries);

      // Filter for full tank events
      const batchFullTankEvents = batchAllEntries.filter(entry => entry.isFullTank === true);

      console.log(`Found ${batchFullTankEvents.length} full tank events and ${batchAllEntries.length} total entries in this batch`);

      // Add full tank events to our collection
      fullTankEvents.push(...batchFullTankEvents);

      // If we have enough full tank events, stop
      if (fullTankEvents.length >= count) {
        console.log(`Found enough full tank events (${fullTankEvents.length}). Stopping.`);
        break;
      }

      // Move to next batch (going further back in time)
      currentEndDate = batchStartDate;
      batchCount++;
    }

    // Sort full tank events by date (descending - newest first) and take the first N
    const sortedEvents = fullTankEvents
      .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
      .slice(0, count);

    // Deduplicate all entries by ID or date+voucher combination to avoid counting same entry twice
    const seenEntries = new Map();
    const deduplicatedAllEntries = allFuelEntries.filter(entry => {
      // Create a unique key: use _id if available, otherwise use date+voucher+quantity
      const entryKey = entry._id || `${moment(entry.date).valueOf()}-${entry.voucherNo || ''}-${entry.quantity || 0}`;
      if (seenEntries.has(entryKey)) {
        return false; // Skip duplicate
      }
      seenEntries.set(entryKey, true);
      return true;
    });
    
    // Sort all entries by date (descending - newest first)
    const sortedAllEntries = deduplicatedAllEntries
      .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());

    console.log(`Returning ${sortedEvents.length} full tank events and ${sortedAllEntries.length} total entries (after deduplication from ${allFuelEntries.length})`);

    return { 
      success: true, 
      events: sortedEvents,
      allEntries: sortedAllEntries, // Include all entries for consumption calculation
      error: null 
    };
  } catch (error) {
    console.error('Error fetching last full tank events:', error);
    return { 
      success: false, 
      events: [], 
      allEntries: [],
      error: error.message 
    };
  }
};

/**
 * Transform API fuel entry data to trend data format
 * @param {Array} apiEntries - Array of FULL TANK fuel entries from API (sorted newest first)
 * @param {number} standardRate - Standard consumption rate (optional)
 * @param {string} standardUnit - Standard unit from model: 'L_PER_KM', 'L_PER_HOUR', or 'L_PER_KWH' (optional)
 * @param {Array} allFuelEntries - Array of ALL fuel entries (full tank and non-full tank) for calculating total fuel consumed (optional)
 * @returns {Array} Transformed trend data (sorted newest first - descending order)
 */
// Helper function to extract calculated reading from mileage
// Prefers calculatedReading from meterReadingRecord (cumulative, always increasing)
// Falls back to reading (physical meter reading, may reset on meter change)
const getCalculatedReading = (mileage) => {
  if (!mileage) return null;
  // Check for calculatedReading in meterReadingRecord first
  if (mileage.meterReadingRecord && 
      mileage.meterReadingRecord.calculatedReading !== null && 
      mileage.meterReadingRecord.calculatedReading !== undefined) {
    return mileage.meterReadingRecord.calculatedReading;
  }
  // Fall back to regular reading
  return mileage.reading;
};

// Helper function to get both readings for display purposes
const getMeterReadings = (mileage) => {
  if (!mileage) return { reading: null, calculatedReading: null };
  const reading = mileage.reading;
  const calculatedReading = mileage.meterReadingRecord?.calculatedReading ?? null;
  return { reading, calculatedReading };
};

/**
 * Meter priority used across the fuel reports.
 *
 * An asset can carry more than one meter on the same fuel entry (a DG with both a KWH
 * and an Hours meter, a vehicle with both an Odometer and an Hours meter). The standard
 * consumption rate is always calculated on the HIGHEST priority meter present:
 *
 *   KWH  (highest)  ->  standard unit L_PER_KWH
 *   KMS             ->  standard unit L_PER_KM
 *   HOURS (lowest)  ->  standard unit L_PER_HOUR
 *
 * The lower-priority meters are still captured so the trend table can show every reading
 * that actually exists on the entry, and so Operating Level can be derived from them.
 */
export const METER_PRIORITY = ['KWH', 'KMS', 'HOURS'];

/**
 * Display labels per meter type.
 *   runLabel  - column/row label for the meter movement between two full tanks
 *   unit      - unit suffix shown next to a run value
 *   rateUnit  - singular unit used when the type appears inside a ratio (KWH/Hour, KM/Hour)
 */
export const METER_LABELS = {
  KWH: { runLabel: 'KWH Consumed', unit: 'KWH', rateUnit: 'KWH' },
  KMS: { runLabel: 'KMs Run', unit: 'KMs', rateUnit: 'KM' },
  HOURS: { runLabel: 'Hours Run', unit: 'HOURS', rateUnit: 'Hour' },
};

// Map a standard unit from the asset model onto the meter type it is calculated on.
export const meterTypeForStandardUnit = (standardUnit) => {
  if (standardUnit === 'L_PER_KWH') return 'KWH';
  if (standardUnit === 'L_PER_KM') return 'KMS';
  if (standardUnit === 'L_PER_HOUR') return 'HOURS';
  return null;
};

// Strict name match — a meter is only treated as KWH/KMS/HOURS when its own name says so.
// No fallback to "the first meter on the entry": pairing a reading with the wrong meter
// type would silently produce a meaningless run value.
const meterMatchesType = (meter, type) => {
  if (!meter || !meter.name) return false;
  const name = meter.name.toUpperCase();
  if (type === 'KWH') return name.includes('KWH');
  if (type === 'KMS') {
    return name.includes('KM') || name.includes('ODO') || name.includes('DISTANCE');
  }
  if (type === 'HOURS') return name.includes('HOUR') || name.includes('HRS');
  return false;
};

export const findMeterByType = (mileage, type) => {
  if (!mileage || mileage.length === 0) return null;
  return mileage.find((m) => meterMatchesType(m, type)) || null;
};

/**
 * Run (movement) of every named meter between two fuel entries.
 *
 * Returns a plain object keyed by meter type, e.g. { KWH: 1250, HOURS: 41.5 }. A type is
 * only present when BOTH entries carry that meter with a usable reading — a missing meter
 * on either side leaves the key absent rather than defaulting to zero.
 */
export const getMeterRunsBetween = (currentMileage, previousMileage) => {
  const runs = {};
  METER_PRIORITY.forEach((type) => {
    const currentReading = getCalculatedReading(findMeterByType(currentMileage, type));
    const previousReading = getCalculatedReading(findMeterByType(previousMileage, type));
    if (
      currentReading !== null && currentReading !== undefined &&
      previousReading !== null && previousReading !== undefined
    ) {
      runs[type] = currentReading - previousReading;
    }
  });
  return runs;
};

/**
 * Operating Level = run on the highest-priority meter divided by the run on the
 * lowest-priority meter available for the SAME interval.
 *
 *   DG      (KWH + HOURS)  ->  KWH/Hour  - power load carried by the DG
 *   Vehicle (KMS + HOURS)  ->  KM/Hour   - average speed
 *
 * It is an operating-intensity indicator only; it never feeds the standard/actual rate or
 * the deviation. When fewer than two meters moved over the interval, or the denominator is
 * not usable, the value is null and `reason` explains why — the report shows the reason
 * instead of substituting zero.
 *
 * @param {Object} meterRuns - output of getMeterRunsBetween (or an equivalent object)
 * @returns {{value: number|null, numeratorType: ?string, denominatorType: ?string,
 *            unitLabel: ?string, numerator: ?number, denominator: ?number, reason: ?string}}
 */
export const computeOperatingLevel = (meterRuns) => {
  const available = METER_PRIORITY.filter(
    (type) => meterRuns && typeof meterRuns[type] === 'number' && Number.isFinite(meterRuns[type])
  );

  const empty = {
    value: null,
    numeratorType: null,
    denominatorType: null,
    unitLabel: null,
    numerator: null,
    denominator: null,
  };

  if (available.length === 0) {
    return { ...empty, reason: 'No meter reading' };
  }
  if (available.length === 1) {
    return { ...empty, reason: `Only ${METER_LABELS[available[0]].unit} recorded` };
  }

  const numeratorType = available[0];
  const denominatorType = available[available.length - 1];
  const numerator = meterRuns[numeratorType];
  const denominator = meterRuns[denominatorType];
  const unitLabel = `${METER_LABELS[numeratorType].rateUnit}/${METER_LABELS[denominatorType].rateUnit}`;
  const base = { numeratorType, denominatorType, unitLabel, numerator, denominator };

  if (denominator <= 0) {
    return {
      ...base,
      value: null,
      reason: `${METER_LABELS[denominatorType].unit} run is ${Number(denominator.toFixed(2))}`,
    };
  }
  if (numerator < 0) {
    return {
      ...base,
      value: null,
      reason: `${METER_LABELS[numeratorType].unit} run is negative`,
    };
  }

  return { ...base, value: numerator / denominator, reason: null };
};

/**
 * Operating Level expressed as a percentage of a stated maximum — e.g. a DG's rated
 * KWH/Hour, or a vehicle's maximum KM/Hour.
 *
 * Returns null (no percentage shown) unless ALL of these hold:
 *   - the row actually has an Operating Level value
 *   - a maximum greater than zero has been supplied
 *   - the maximum is expressed in the SAME unit as the row
 *
 * The unit check is deliberate: measuring a KM/Hour row against a KWH/Hour maximum
 * would produce a confident-looking but meaningless number, so it is refused instead.
 *
 * @param {Object} operatingLevel - a computeOperatingLevel result
 * @param {number} maxOperatingLevel - the stated maximum
 * @param {string} maxUnitLabel - unit the maximum was entered against (e.g. 'KWH/Hour')
 * @returns {number|null} percentage of the maximum, or null when it cannot be stated
 */
export const operatingLevelPercentOfMax = (operatingLevel, maxOperatingLevel, maxUnitLabel) => {
  if (!operatingLevel || operatingLevel.value === null || operatingLevel.value === undefined) {
    return null;
  }
  if (typeof maxOperatingLevel !== 'number' || !Number.isFinite(maxOperatingLevel) || maxOperatingLevel <= 0) {
    return null;
  }
  if (!maxUnitLabel || operatingLevel.unitLabel !== maxUnitLabel) {
    return null;
  }
  return (operatingLevel.value / maxOperatingLevel) * 100;
};

// Display bands for Operating Level as a percentage of the stated maximum. These are
// presentation thresholds only — they colour the figure, they never change it, and they
// have no bearing on the fuel deviation or the alert status. Named constants so they can
// move onto the asset model alongside the maximum itself.
export const OPERATING_LEVEL_OVER_MAX_PERCENT = 100; // above this the rated maximum is exceeded
export const OPERATING_LEVEL_LOW_PERCENT = 50;       // below this the asset is running lightly loaded

/**
 * Which display band a percentage-of-maximum falls into.
 *
 *   OVER_MAX - above 100%: the asset ran beyond its rated maximum
 *   LOW      - below 50%: lightly loaded (a DG idling on a small load, a vehicle barely moving)
 *   NORMAL   - 50% to 100% inclusive
 *
 * Returns null when there is no percentage to band, so the caller shows nothing rather
 * than defaulting to a band.
 *
 * @param {number|null} percentOfMax - output of operatingLevelPercentOfMax
 * @returns {'OVER_MAX'|'LOW'|'NORMAL'|null}
 */
export const operatingLevelBand = (percentOfMax) => {
  if (typeof percentOfMax !== 'number' || !Number.isFinite(percentOfMax)) {
    return null;
  }
  if (percentOfMax > OPERATING_LEVEL_OVER_MAX_PERCENT) return 'OVER_MAX';
  if (percentOfMax < OPERATING_LEVEL_LOW_PERCENT) return 'LOW';
  return 'NORMAL';
};

export const transformToTrendData = (apiEntries, standardRate = null, standardUnit = null, allFuelEntries = null) => {
  if (!apiEntries || apiEntries.length === 0) {
    return [];
  }

  // Keep entries in descending order (newest first) for table display
  const sortedEntries = [...apiEntries];
  
  // Sort all fuel entries by date (ascending - oldest first) for finding intermediate entries
  const sortedAllEntries = allFuelEntries 
    ? [...allFuelEntries].sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
    : null;

  return sortedEntries.map((entry, index) => {
    // Determine meter type based on STANDARD UNIT from model (not meter availability)
    // If no standard unit provided, fall back to detecting from first meter
    let isKmBased = false;
    let isHoursBased = false;
    let isKwhBased = false;
    
    if (standardUnit) {
      // Use standard unit from model to determine which meter to use
      isKmBased = standardUnit === 'L_PER_KM';
      isHoursBased = standardUnit === 'L_PER_HOUR';
      isKwhBased = standardUnit === 'L_PER_KWH';
    } else {
      // Fall back to detecting from first meter (old behavior)
      const sampleMeter = entry.mileage && entry.mileage.length > 0 ? entry.mileage[0] : null;
      isKmBased = sampleMeter && sampleMeter.name && 
        (sampleMeter.name.toUpperCase().includes('KM') || 
         sampleMeter.name.toUpperCase().includes('KMS') || 
         sampleMeter.name.toUpperCase().includes('ODO') ||
         sampleMeter.name.toUpperCase().includes('DISTANCE'));
      isHoursBased = sampleMeter && sampleMeter.name && 
        (sampleMeter.name.toUpperCase().includes('HOUR') || 
         sampleMeter.name.toUpperCase().includes('HRS'));
      isKwhBased = sampleMeter && sampleMeter.name && 
        sampleMeter.name.toUpperCase().includes('KWH');
    }

    // Calculate run distance/hours: Current meter reading - Previous full tank event's meter reading
    // For each event, run = current meter reading - previous event's meter reading
    // When sorted newest first, previous (older) event is at index + 1
    let runValue = null; // Can be kmsRun or hoursRun depending on asset type
    let hoursRunValue = null; // Also extract hoursRun for KWH and KM assets for comparison
    let kmsRunValue = null; // Also extract kmsRun for comparison
    let meterRuns = {}; // Run on every named meter present on both entries, keyed by meter type
    
    if (index < sortedEntries.length - 1) {
      const previousEntry = sortedEntries[index + 1]; // Previous entry in time (older, at next index)
      
      // Find appropriate meter reading based on standard unit (not meter availability)
      let currentMileage = null;
      let previousMileage = null;
      let currentHoursMileage = null;
      let previousHoursMileage = null;
      
      if (isKmBased) {
        // Look for KM/KMS/ODO meter
        currentMileage = entry.mileage && entry.mileage.length > 0
          ? entry.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('KM') || 
                        m.name.toUpperCase().includes('KMS') || 
                        m.name.toUpperCase().includes('ODO') ||
                        m.name.toUpperCase().includes('DISTANCE'))
            ) || entry.mileage[0] // Fallback to first mileage
          : null;
        
        previousMileage = previousEntry.mileage && previousEntry.mileage.length > 0
          ? previousEntry.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('KM') || 
                        m.name.toUpperCase().includes('KMS') || 
                        m.name.toUpperCase().includes('ODO') ||
                        m.name.toUpperCase().includes('DISTANCE'))
            ) || previousEntry.mileage[0] // Fallback to first mileage
          : null;
        
        // Also extract hours meter for comparison
        currentHoursMileage = entry.mileage && entry.mileage.length > 0
          ? entry.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
            ) || null
          : null;
        
        previousHoursMileage = previousEntry.mileage && previousEntry.mileage.length > 0
          ? previousEntry.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
            ) || null
          : null;
      } else if (isHoursBased) {
        // Look for HOURS meter
        currentMileage = entry.mileage && entry.mileage.length > 0
          ? entry.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
            ) || entry.mileage[0] // Fallback to first mileage
          : null;
        
        previousMileage = previousEntry.mileage && previousEntry.mileage.length > 0
          ? previousEntry.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
            ) || previousEntry.mileage[0] // Fallback to first mileage
          : null;
      } else if (isKwhBased) {
        // Look for KWH meter
        currentMileage = entry.mileage && entry.mileage.length > 0
          ? entry.mileage.find(m => 
              m.name && m.name.toUpperCase().includes('KWH')
            ) || entry.mileage[0] // Fallback to first mileage
          : null;
        
        previousMileage = previousEntry.mileage && previousEntry.mileage.length > 0
          ? previousEntry.mileage.find(m => 
              m.name && m.name.toUpperCase().includes('KWH')
            ) || previousEntry.mileage[0] // Fallback to first mileage
          : null;
        
        // Also extract hours meter for comparison
        currentHoursMileage = entry.mileage && entry.mileage.length > 0
          ? entry.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
            ) || null
          : null;
        
        previousHoursMileage = previousEntry.mileage && previousEntry.mileage.length > 0
          ? previousEntry.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
            ) || null
          : null;
      } else {
        // Fallback: use first meter
        currentMileage = entry.mileage && entry.mileage.length > 0 ? entry.mileage[0] : null;
        previousMileage = previousEntry.mileage && previousEntry.mileage.length > 0 ? previousEntry.mileage[0] : null;
      }
      
      // Use calculatedReading for run value calculation (cumulative, always increasing)
      // This handles cases where physical meter was reset (e.g., equipment change)
      const currentCalcReading = getCalculatedReading(currentMileage);
      const previousCalcReading = getCalculatedReading(previousMileage);
      
      if (currentCalcReading !== null && previousCalcReading !== null) {
        runValue = currentCalcReading - previousCalcReading;
      }
      
      // Calculate hoursRun for comparison if hours meter is available
      const currentHoursCalcReading = getCalculatedReading(currentHoursMileage);
      const previousHoursCalcReading = getCalculatedReading(previousHoursMileage);
      
      if (currentHoursCalcReading !== null && previousHoursCalcReading !== null) {
        hoursRunValue = currentHoursCalcReading - previousHoursCalcReading;
      }

      // Capture the run on EVERY named meter the pair of entries carries, not just the
      // priority meter the standard rate is calculated on. The trend table shows all of
      // them and derives Operating Level from them.
      meterRuns = getMeterRunsBetween(entry.mileage, previousEntry.mileage);
    }

    // Calculate total fuel consumed between previous full tank and current full tank
    // Total fuel = current full tank quantity + all non-full tank entries between previous and current
    let fuelAdded = entry.quantity || 0;
    let intermediateFuelEntries = [];
    
    if (sortedAllEntries && index < sortedEntries.length - 1) {
      const previousEntry = sortedEntries[index + 1]; // Previous full tank event (older)
      const currentEntryDate = moment(entry.date);
      const previousEntryDate = moment(previousEntry.date);
      
      // Find all non-full-tank entries between previous and current full tank events
      // These are entries that are AFTER previous full tank AND BEFORE current full tank
      intermediateFuelEntries = sortedAllEntries.filter(e => {
        if (e.isFullTank) return false; // Skip full tank entries
        const entryDate = moment(e.date);
        // Entry must be after previous full tank and before (or at) current full tank
        // Exclude entries at exactly the same time as the full tank events
        return entryDate.isAfter(previousEntryDate) && entryDate.isBefore(currentEntryDate);
      });
      
      if (intermediateFuelEntries.length > 0) {
        const intermediateFuel = intermediateFuelEntries.reduce((sum, e) => sum + (e.quantity || 0), 0);
        fuelAdded = fuelAdded + intermediateFuel;
        console.log(`[transformToTrendData] Entry ${index + 1} (${entry.voucherNo || moment(entry.date).format('YYYY-MM-DD')}): ` +
          `Full tank: ${entry.quantity}L + Intermediate (${intermediateFuelEntries.length} entries): ${intermediateFuel}L = Total: ${fuelAdded}L`);
        console.log(`  Intermediate entries between ${previousEntryDate.format('YYYY-MM-DD HH:mm')} and ${currentEntryDate.format('YYYY-MM-DD HH:mm')}:`);
        intermediateFuelEntries.forEach((e, i) => {
          console.log(`    ${i + 1}. ${moment(e.date).format('YYYY-MM-DD HH:mm')} - ${e.voucherNo || 'N/A'} - ${e.quantity}L`);
        });
      }
    }

    // Calculate actual rate based on standard unit (not meter type)
    // For KM-based: KM/L = kmsRun / fuelAdded
    // For HOURS-based: L/Hours = fuelAdded / hoursRun
    // For KWH-based: KWH/L = kwhRun / fuelAdded
    let actualRate = null;
    if (isKmBased && runValue !== null && runValue > 0 && fuelAdded > 0) {
      // Calculate KM/L
      actualRate = runValue / fuelAdded;
    } else if (isHoursBased && runValue !== null && runValue > 0 && fuelAdded > 0) {
      // Calculate L/Hours
      actualRate = fuelAdded / runValue;
    } else if (isKwhBased && runValue !== null && runValue > 0 && fuelAdded > 0) {
      // Calculate KWH/L
      actualRate = runValue / fuelAdded;
    }

    // Calculate deviation if standard rate is provided
    // For KM/L and KWH/L: higher is better, so invert (negative = good)
    // For L/HOURS: lower is better, so normal (negative = good)
    let deviation = null;
    if (actualRate !== null && standardRate !== null && standardRate > 0) {
      if (isKmBased || isKwhBased) {
        // Higher is better, invert deviation
        deviation = ((standardRate - actualRate) / standardRate) * 100;
      } else {
        // Lower is better, normal deviation
        deviation = ((actualRate - standardRate) / standardRate) * 100;
      }
    }

    // Get meter reading (odo) - prefer appropriate meter based on standard unit
    // Include both physical reading and calculatedReading for display
    let odo = null;
    let odoPhysical = null; // Physical meter reading (may reset on meter change)
    let odoCalculated = null; // Calculated reading (cumulative, always increasing)
    let meterDetails = null; // Full meter details for breakup display
    
    if (entry.mileage && entry.mileage.length > 0) {
      let selectedMeter = null;
      
      if (isKmBased) {
        selectedMeter = entry.mileage.find(m => 
          m.name && (m.name.toUpperCase().includes('KM') || 
                    m.name.toUpperCase().includes('KMS') || 
                    m.name.toUpperCase().includes('ODO') ||
                    m.name.toUpperCase().includes('DISTANCE'))
        ) || entry.mileage[0];
      } else if (isHoursBased) {
        selectedMeter = entry.mileage.find(m => 
          m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
        ) || entry.mileage[0];
      } else if (isKwhBased) {
        selectedMeter = entry.mileage.find(m => 
          m.name && m.name.toUpperCase().includes('KWH')
        ) || entry.mileage[0];
      } else {
        selectedMeter = entry.mileage[0];
      }
      
      if (selectedMeter) {
        const readings = getMeterReadings(selectedMeter);
        odoPhysical = readings.reading;
        odoCalculated = readings.calculatedReading;
        // Use calculatedReading for odo (for calculations), fall back to physical reading
        odo = odoCalculated ?? odoPhysical;
        
        // Store meter details for breakup display
        meterDetails = {
          name: selectedMeter.name,
          reading: readings.reading,
          calculatedReading: readings.calculatedReading,
          hasCalculatedReading: readings.calculatedReading !== null,
        };
      }
    }

    // The priority meter's run is the one the standard rate is calculated on. Keep it as
    // the authoritative value for its own meter type so the table's primary reading and the
    // Operating Level always agree with the rate shown on the same row.
    const primaryMeterType = isKwhBased ? 'KWH' : (isKmBased ? 'KMS' : (isHoursBased ? 'HOURS' : null));
    if (primaryMeterType && runValue !== null && runValue !== undefined) {
      meterRuns[primaryMeterType] = runValue;
    }

    const operatingLevel = computeOperatingLevel(meterRuns);

    // Calculate intermediate fuel once to avoid double calculation
    const intermediateFuel = intermediateFuelEntries.reduce((sum, e) => sum + (e.quantity || 0), 0);
    
    // Store intermediate entries details for breakdown popup
    const intermediateEntriesDetails = intermediateFuelEntries.map(e => ({
      date: moment(e.date).format('YYYY-MM-DD HH:mm:ss'),
      voucherNo: e.voucherNo || '-',
      quantity: e.quantity || 0,
      isFullTank: e.isFullTank || false,
    }));

    return {
      id: index + 1,
      refuelTime: moment(entry.date).format('YYYY-MM-DD HH:mm:ss'),
      voucherNo: entry.voucherNo || '',
      hoursRun: isHoursBased ? runValue : (hoursRunValue !== null ? hoursRunValue : null), // Primary for hours-based, secondary for KWH/KM assets
      kmsRun: isKmBased ? runValue : null, // Add kmsRun for KM-based assets
      kwhRun: isKwhBased ? runValue : null, // Add kwhRun for KWH-based assets
      fuelAdded: fuelAdded, // Total fuel consumed (full tank + intermediate non-full-tank entries)
      fullTankFuel: entry.quantity || 0, // Fuel added at this full tank only
      intermediateFuelCount: intermediateFuelEntries.length, // Number of intermediate entries
      intermediateFuel: intermediateFuel, // Total intermediate fuel
      intermediateEntriesDetails: intermediateEntriesDetails, // Details for breakdown popup
      actualRate: actualRate, // Now in KM/L for KM-based, L/Hours for hours-based, KWH/L for KWH-based
      standardRate: standardRate,
      deviation: deviation,
      meterRuns: meterRuns, // Run of every named meter over this interval, keyed by meter type
      primaryMeterType: primaryMeterType, // Meter type the standard/actual rate is calculated on
      operatingLevel: operatingLevel, // Higher-priority run / lower-priority run (e.g. KWH/Hour)
      odo: odo, // calculatedReading (cumulative) - used for calculations
      odoPhysical: odoPhysical, // Physical meter reading (may reset on meter change)
      odoCalculated: odoCalculated, // Calculated reading from meterReadingRecord
      meterDetails: meterDetails, // Full meter details for breakup display
      // Include full entry data for reference
      _apiEntry: entry,
    };
  });
};

/**
 * Fetch daily IoT fuel data summary for all devices in a period
 * @param {number} startTimestamp - Start timestamp in milliseconds
 * @param {number} endTimestamp - End timestamp in milliseconds
 * @param {Object} filters - Filter options
 * @param {Array|null} filters.assets - Array of asset IDs or null for all
 * @param {Array} filters.locations - Array of location codes or ["all"]
 * @param {Array} filters.subLocations - Array of sub-location codes or ["all"]
 * @param {Array} filters.assetTypes - Array of asset type IDs
 * @param {Array} filters.assetSubTypes - Array of asset sub-type IDs
 * @param {Array} filters.assetModels - Array of asset model IDs
 * @param {string} filters.frequency - Report frequency (typically "Daily")
 * @returns {Promise<{success: boolean, data: Array, error: string|null}>}
 */
export const fetchDailyFuelDataReport = async (startTimestamp, endTimestamp, filters = {}) => {
  try {
    console.log(`Fetching daily fuel data report from ${startTimestamp} to ${endTimestamp}`);
    
    // Check if period is valid
    if (endTimestamp <= startTimestamp) {
      return {
        success: false,
        data: [],
        error: 'End timestamp must be after start timestamp'
      };
    }

    const endpoint = `/daily-fuel-data/all-device-report/${startTimestamp}/${endTimestamp}`;
    
    // Default payload
    const payload = {
      assets: filters.assets || null,
      locations: filters.locations || ["all"],
      subLocations: filters.subLocations || ["all"],
      assetTypes: filters.assetTypes || [],
      assetSubTypes: filters.assetSubTypes || [],
      assetModels: filters.assetModels || [],
      frequency: filters.frequency || "Daily"
    };
    
    const result = await apiPost(endpoint, payload);

    if (!result.success) {
      return { success: false, data: [], error: result.error };
    }

    console.log('Daily Fuel Data Report API Response:', {
      hasData: !!result.data,
      dataType: Array.isArray(result.data) ? 'array' : typeof result.data,
      dataLength: Array.isArray(result.data) ? result.data.length : 'N/A',
      sampleRecord: Array.isArray(result.data) && result.data.length > 0 ? result.data[0] : null
    });

    return { success: true, data: result.data || [], error: null };
  } catch (error) {
    console.error('Error fetching daily fuel data report:', error);
    return { success: false, data: [], error: error.message };
  }
};

/**
 * Fetch full tank events for an asset within a date period and calculate consumption rates
 * @param {string} assetCode - Asset code (e.g., '20E-290')
 * @param {number} startTimestamp - Start timestamp in milliseconds
 * @param {number} endTimestamp - End timestamp in milliseconds
 * @returns {Promise<{success: boolean, events: Array, count: number, avgConsumptionRate: number|null, avgConsumptionRateUnit: string|null, error: string|null}>}
 */
export const fetchFullTankEventsForPeriod = async (assetCode, startTimestamp, endTimestamp) => {
  try {
    console.log(`Fetching full tank events for asset ${assetCode} from ${startTimestamp} to ${endTimestamp}`);

    // Step 1: Find asset ID
    const assetResult = await findAssetIdByCode(assetCode);
    if (!assetResult.success) {
      return { success: false, events: [], count: 0, avgConsumptionRate: null, avgConsumptionRateUnit: null, error: assetResult.error };
    }

    const assetId = assetResult.assetId;
    console.log(`Found asset ID: ${assetId}`);

    // Step 2: Convert timestamps to ISO strings
    const startMoment = moment(startTimestamp);
    const endMoment = moment(endTimestamp);

    if (!startMoment.isValid() || !endMoment.isValid()) {
      return {
        success: false,
        events: [],
        count: 0,
        avgConsumptionRate: null,
        avgConsumptionRateUnit: null,
        error: 'Invalid timestamp format'
      };
    }

    console.log(`Fetching fuel entries from ${startMoment.toISOString()} to ${endMoment.toISOString()}`);

    // Step 3: Fetch fuel entries for the period
    const result = await fetchFuelEntries(
      assetId,
      startMoment.toISOString(),
      endMoment.toISOString()
    );

    if (!result.success) {
      return {
        success: false,
        events: [],
        count: 0,
        avgConsumptionRate: null,
        avgConsumptionRateUnit: null,
        error: result.error
      };
    }

    // Step 4: Filter for full tank events within the period
    const fullTankEvents = result.data
      .filter(entry => {
        const entryDate = moment(entry.date);
        return entry.isFullTank === true && 
               entryDate.isBetween(startMoment, endMoment, null, '[]'); // Inclusive
      })
      .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf()); // Sort ascending (oldest first)

    console.log(`Found ${fullTankEvents.length} full tank events in period`);

    if (fullTankEvents.length === 0) {
      return {
        success: true,
        events: [],
        count: 0,
        avgConsumptionRate: null,
        avgConsumptionRateUnit: null,
        error: null
      };
    }

    // Step 5: Calculate consumption rates between consecutive full tank events
    const consumptionRates = [];
    let consumptionRateUnit = null;

    // Determine asset type from first entry's meter
    const sampleMeter = fullTankEvents[0].mileage && fullTankEvents[0].mileage.length > 0 
      ? fullTankEvents[0].mileage[0] 
      : null;
    const isKmBased = sampleMeter && sampleMeter.name && 
      (sampleMeter.name.toUpperCase().includes('KM') || 
       sampleMeter.name.toUpperCase().includes('KMS') || 
       sampleMeter.name.toUpperCase().includes('ODO') ||
       sampleMeter.name.toUpperCase().includes('DISTANCE'));
    const isHoursBased = sampleMeter && sampleMeter.name && 
      (sampleMeter.name.toUpperCase().includes('HOUR') || 
       sampleMeter.name.toUpperCase().includes('HRS'));

    // Set unit based on asset type
    if (isKmBased) {
      consumptionRateUnit = 'KM/L';
    } else if (isHoursBased) {
      consumptionRateUnit = 'L/HOURS';
    }

    // Full Tank to Full Tank Logic:
    // Consumption rate is calculated from PREVIOUS full tank event to CURRENT full tank event
    // - Fuel Consumed = Current full tank event quantity + all non-full tank events between previous and current
    // - Fuel added in previous full tank event is NOT included
    // - Meter Run = Current meter reading - Previous meter reading
    
    // Calculate consumption rate for each full tank event (starting from the second one, as first has no previous)
    for (let i = 1; i < fullTankEvents.length; i++) {
      const currentEvent = fullTankEvents[i];
      const previousEvent = fullTankEvents[i - 1];

      // Get meter readings for current and previous events
      let currentMileage = null;
      let previousMileage = null;

      if (isKmBased) {
        currentMileage = currentEvent.mileage && currentEvent.mileage.length > 0
          ? currentEvent.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('KM') || 
                        m.name.toUpperCase().includes('KMS') || 
                        m.name.toUpperCase().includes('ODO') ||
                        m.name.toUpperCase().includes('DISTANCE'))
            ) || currentEvent.mileage[0]
          : null;
        
        previousMileage = previousEvent.mileage && previousEvent.mileage.length > 0
          ? previousEvent.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('KM') || 
                        m.name.toUpperCase().includes('KMS') || 
                        m.name.toUpperCase().includes('ODO') ||
                        m.name.toUpperCase().includes('DISTANCE'))
            ) || previousEvent.mileage[0]
          : null;
      } else if (isHoursBased) {
        currentMileage = currentEvent.mileage && currentEvent.mileage.length > 0
          ? currentEvent.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
            ) || currentEvent.mileage[0]
          : null;
        
        previousMileage = previousEvent.mileage && previousEvent.mileage.length > 0
          ? previousEvent.mileage.find(m => 
              m.name && (m.name.toUpperCase().includes('HOUR') || m.name.toUpperCase().includes('HRS'))
            ) || previousEvent.mileage[0]
          : null;
      } else {
        // Fallback: use first meter
        currentMileage = currentEvent.mileage && currentEvent.mileage.length > 0 ? currentEvent.mileage[0] : null;
        previousMileage = previousEvent.mileage && previousEvent.mileage.length > 0 ? previousEvent.mileage[0] : null;
      }

      // Calculate run value (distance or hours): Current - Previous
      if (currentMileage && previousMileage && 
          currentMileage.reading !== null && previousMileage.reading !== null) {
        const runValue = currentMileage.reading - previousMileage.reading;
        
        // Get fuel consumed: Current full tank event + all non-full tank events between previous and current
        // Note: Previous full tank event quantity is NOT included
        const currentEventDate = moment(currentEvent.date);
        const previousEventDate = moment(previousEvent.date);
        
        // Sum: Current full tank event + all entries after previous event but before current event
        const fuelConsumed = result.data
          .filter(entry => {
            const entryDate = moment(entry.date);
            // Include current full tank event and all entries after previous event but before current event
            return (entryDate.isSame(currentEventDate) || 
                    (entryDate.isAfter(previousEventDate) && entryDate.isBefore(currentEventDate)));
          })
          .reduce((sum, entry) => sum + (entry.quantity || 0), 0);

        if (runValue > 0 && fuelConsumed > 0) {
          let rate = null;
          if (isKmBased) {
            // KM/L = runValue / fuelConsumed
            rate = runValue / fuelConsumed;
          } else if (isHoursBased) {
            // L/HOURS = fuelConsumed / runValue
            rate = fuelConsumed / runValue;
          }
          
          if (rate !== null && rate > 0) {
            consumptionRates.push(rate);
          }
        }
      }
    }

    // Calculate average consumption rate
    let avgConsumptionRate = null;
    if (consumptionRates.length > 0) {
      const sum = consumptionRates.reduce((acc, rate) => acc + rate, 0);
      avgConsumptionRate = sum / consumptionRates.length;
    }

    console.log(`Calculated ${consumptionRates.length} consumption rates, average: ${avgConsumptionRate}`);

    return {
      success: true,
      events: fullTankEvents,
      count: fullTankEvents.length,
      avgConsumptionRate: avgConsumptionRate,
      avgConsumptionRateUnit: consumptionRateUnit,
      consumptionRates: consumptionRates,
      error: null
    };
  } catch (error) {
    console.error('Error fetching full tank events for period:', error);
    return {
      success: false,
      events: [],
      count: 0,
      avgConsumptionRate: null,
      avgConsumptionRateUnit: null,
      error: error.message
    };
  }
};
